// Purpose: the code-side vocabulary of the NEW per-action permission system.
//
// It is the ONLY permission system enforced. `permissionList.ts` beside it is the legacy NAME list,
// and no name in it is checked anywhere any more: the two share no tables, types or grant paths, and
// what survives of v1/v2 is TABLES ONLY, kept so a rollback has something to read and so "what did this
// person used to hold" stays answerable. There is no flag and no fallback — a missing grant is a
// refusal, not a silent pass to a second system. See
// server/graph/lib/services/permissionsTransition/permissionGate.ts.
//
// A permission is a (resource, action) pair and its key is DERIVED from that pair rather
// than written down twice. The database derives `permissions_transition.Name` the same way, as a
// STORED generated column, so the two cannot drift apart.
//
// Both dimensions are real DB enums, so the database rejects a resource or action this file does
// not declare. Adding either takes a manual migration in server/graph/sqlUpdatesManual/; there is no
// runtime creation, so a permission with no row has no grants and is dead at runtime, exactly as in
// the existing system.
//
// The vocabulary below covers the whole authenticated GraphQL surface of both subgraphs — every
// `Query` and `Mutation` root field in server/graph and server/hiring. Each of those fields declares
// the pair it needs with `@requiresPermission` in its own SDL, or declares that it deliberately needs
// none with `@noPermissionRequired(reason:)`; a field carrying neither fails the exhaustiveness check
// in server/graph/lib/services/permissionsTransition/fieldPermissions.test.ts. So this list and the
// surface are kept in step by CI rather than by discipline.
//
// It also covers the entry points that have NO GraphQL surface and so can carry no directive: the
// twelve upload/download/report REST routes, the MCP surface, and
// hiring's own `/query` door. Those name their permission inline at the guard site, so nothing keeps
// them in step automatically — read
// server/graph/lib/services/permissionsTransition/AGENTS.md before changing one.
//
// Enumerating the two dimensions rather than the keys is what keeps that affordable: 166 resources and
// 52 actions describe 381 declared permissions, and adding one more is an INSERT — only a genuinely
// new noun or verb costs an ALTER.
//
// A resource or action with no declared pair would be dead vocabulary, so there is none: every entry
// in both lists is used by at least one permission below.
//
// WHAT ONE OF THESE PERMISSIONS AUTHORISES — read this before granting any of them.
//
// A permission here authorises CALLING an endpoint. It does not decide WHICH ROWS that endpoint
// returns or touches; that scoping stays inside the resolver, where it already lives, and still runs.
// `app.pto` on `Query.ptoRequests` means "may call it", not "may read anyone's PTO" — the
// resolver goes on limiting the result to your own requests, your team's, or everyone's exactly as it
// does today. The two layers compose, and the resolver is the narrower one.
//
// That has a consequence worth stating plainly: most SELF-SERVICE endpoints need permissions too, so
// there is a baseline every employee must hold. `Mutation.acceptDocument` declares `handbook.view`.
// Nobody can use the portal at all without those, so the group granting the baseline set has to exist
// and resolve for everybody — it is built by
// 20261029-PRE-1-permissions.sql, which must be applied BEFORE the code deploys.
//
// A SMALL NUMBER OF FIELDS DECLARE NO PERMISSION AT ALL, with `@noPermissionRequired(reason:)`. That is
// for the cases where a permission could only ever be granted to everyone and so protects nothing:
// session bootstrap (`Query.me`), a person's own notification bell, reference data such as the office
// list, and hello-world. Each one names its reason in the SDL. `Query.me` is why this exists rather
// than a blanket baseline grant: it is what every app calls to establish a session, so an ungranted
// permission there makes the whole portal unusable, and it can never be anything but universal.

import type { NonEmptyPermissions } from './permissionRequirement';

/**
 * The things a permission can act on. Closed vocabulary: a real DB enum backs it, so a
 * new resource needs a migration.
 */
export const PERMISSION_RESOURCES = [
  // Time, absence and travel
  'timesheet',
  'timesheet_reminder',
  'temp_project',
  'pto_request',
  'pto_day',
  // Other people's approved time off, company-wide: the PTO app's calendar page and `ptoRequestsPublic`.
  // `view` is held by everyone the page showed it to — legacy `pto.calendar`, `pto.manager` and
  // `pto.admin` — and the server enforces it, since the API is reachable without the page.
  'pto_calendar',
  // The resourcing STRUCTURE surfaces — org chart, reporting structure, leadership coverage, candidate
  // finder. All five ask for `resourcing.manager` OR `resourcing.admin` (12 active) and nothing in the
  // vocabulary means that: `resourcing.manager` maps to three keys and every one is either far wider
  // (`certification.edit` 139, `certification_image.update` 25) or about something else entirely
  // (`team_pto_request.triage`, which is the right 11 people asked the wrong question).
  'org_structure',
  // The CROSS-PERSON half of PTO. `pto_request.*` is where an employee requests their OWN time off, and
  // that path carries NO key at all: `ptoAdd` expresses it as `orSelf` beside the team keys, because
  // requesting your own PTO is self-service and gating it would lock the whole company out. (It had a
  // baseline `pto_request.create` for a while; a key nothing declares is not a permission, so it was
  // retired.) So every guard that decides whether you may see or act on SOMEBODY ELSE's request gets its
  // own resource, graded by which audience each arm names: `review` is the manager capability, `view` the
  // four permission-only read arms, and `edit` administrator-only.
  'team_pto_request',
  // Somebody else's PTO BALANCE, which is not their request: `Query.ptoCalc` and `Query.ptoEstimate` read
  // a person's accrual, carryover, allowance and remaining days without touching a request at all. Its own
  // resource rather than a seventh `team_pto_request` action because the two audiences differ — reading a
  // balance is the PTO tool's own picker audience, and `team_pto_request.view` is the 23-person
  // unapproved-request tier — and because folding a second capability onto a key whose population happens
  // to overlap is what the rest of this vocabulary exists to undo.
  'team_pto_balance',
  // A balance ADJUSTMENT rather than a request, and superadmin-only: `ptoAdd` refuses an OFFSET a caller
  // makes for themselves unless they are one. Its own resource for the same reason `debug_tool.run` has a
  // grant at all — an audience of one role is still an audience, and writing it down is what makes it
  // reviewable rather than an accident of the superadmin short-circuit.
  'pto_offset',
  // A PTO ENTITLEMENT override on one person, superadmin-only. Its own resource because it shares
  // `entitlement.update` with the two entitlement RULE writes today, whose audience is `permissions.manager`
  // — one key cannot carry both.
  'person_pto_override',
  'work_from_anywhere_request',
  // The CROSS-PERSON half of a travel request, the same split `team_pto_request` makes and for the same
  // reason: booking your own trip is self-service and `newTravelRequest` says so with `orSelf`, while
  // raising one FOR somebody else is `travel.admin` or `travel.moderator` — 13 people. One key for both
  // would have handed the whole company the admin path. (The self side was a baseline
  // `work_from_anywhere_request.create` until the field stopped declaring it; it is retired.)
  'team_work_from_anywhere_request',
  // Somebody else's remaining FLEXIBLE WORK allowance, which is not their request — the same split
  // `team_pto_balance` makes against `team_pto_request`, and for the same reason. `team_work_from_anywhere_request.view`
  // is the unapproved-REQUEST tier at 108 and is already wired to `TravelRequest.listPublic`; pointing an
  // allowance read at it would have had to widen that tier by four people to keep the PTO tool's admins,
  // which is a widening of a live gate rather than a name for this capability.
  'team_work_from_anywhere_balance',
  // The RELATIONSHIP-scope bypass on the travel surface, and the reason it is a resource of its own
  // rather than another `team_work_from_anywhere_request` action: `.view` is the 108-person tier whose rows the
  // resolver still narrows to your own reports, while this one is what lets `TravelRequest.list` reach
  // EVERY employee's requests and resolve any manager's team. Same shape as the `all_*_access` family
  // below, one scope over — that family bypasses the client service team, this bypasses the manager and
  // self relationship.
  'all_work_from_anywhere_request_access',
  // NOT work from anywhere, despite sitting next to it here. A `trip` is the Polaris CRM business-trip
  // planner — an itinerary with billability, attendees and `TripMeeting` rows carrying contact
  // stakeholders and a booking status (apps/polaris/src/pages/Trip). Booking your own remote work is
  // `work_from_anywhere_request`, over the legacy `travel` table. The two share only the English word.
  'trip',
  'assignment_log',
  'blackout_period',
  // People, HR and access control
  'person',
  'person_session',
  'employment_event',
  'offboarding',
  'onboarding',
  'certification',
  'skill_tag',
  'work_history',
  'discipline',
  'department',
  'office',
  'job_title',
  'review',
  // The MANAGER-only review read. `review.view` also names portal.admin and admin.onboarding, because the
  // title-approval queue shares it; `Query.review` (singular) names `app.myteam` alone and has to keep
  // exactly that audience.
  'team_review',
  // The whole-company My Team and Annual Reviews lists, which legacy `teams.superuser` switched on.
  // Apart from `team_review.override` because every Team Manager holds that one, and on OLD their lists
  // showed their own team.
  'all_team_review_access',
  'handbook',
  // The three handbook reads that are superadmin-only in the resolver, where the other three are scoped
  // to the caller's own office and department and gated by nothing. `handbook.view` is BASELINE and has
  // to stay that way for those three, so the admin trio needs a key of its own.
  'all_handbook_access',
  'manager_doc',
  'powerhour',
  'entitlement',
  'permission_group',
  'team_profile',
  // Clients, contacts and delivery
  'client',
  'client_service_team',
  'client_service_map',
  'client_compliance',
  'compliance_workbook',
  'client_sales_meeting',
  'meeting',
  'contact',
  'opportunity',
  'proposal',
  'project',
  'program',
  'contract_template',
  'contract_process',
  'service_group',
  'city',
  'gift_campaign',
  'action_item',
  'comment',
  'note',
  'global_note',
  'document',
  'file',
  'link',
  'portal_tag',
  // Finance
  'invoice',
  'purchase_order',
  'rate_card',
  'revenue_recognition',
  'locked_revenue',
  'billable_hours',
  'forecast',
  'business_days',
  // Reporting
  'report',
  'trend',
  'polaris_metric',
  'opportunity_stat',
  'tva_report',
  // Platform, admin and integrations
  'email_template',
  'email_log',
  'job',
  'notification',
  'notification_setting',
  'slack_message',
  'sql_script',
  'cache',
  // The two superadmin-only tiers under the admin app, each split off a key three or four ungated
  // sibling fields share. Neither could take the sibling's key without widening from 6 to that key's
  // live audience, and neither could narrow the sibling's key without taking a working page away.
  'database_table',
  'admin_metric',
  'vault_file',
  'calendar_event',
  // Events that are not published yet, which are a subset of the rows rather than a verb on them — the
  // same shape `team_pto_request` and `team_work_from_anywhere_request` use to name somebody else's rows. Its own
  // resource because `calendar_event.edit`/`.delete` carry this exact audience but MEAN writing, and an
  // administrator narrowing the write key would silently take the authors' own drafts away from them.
  'unpublished_calendar_event',
  'calendar',
  'ai_assistant',
  'ai_thread',
  'llm_conversation',
  'prompt',
  'license',
  'license_package',
  'license_project',
  'license_scan',
  'linkedin_change_log',
  'planner_user_log',
  'greenhouse_referral',
  'debug_tool',
  // Hiring (the hiring subgraph)
  'hiring_candidate',
  'hiring_test',
  // The three hiring REST routes, which are NOT behind the `app.hiring` door — hiring mounts them on
  // `/api/hiring` under `authMiddleware` alone. Every `hiring_test.*` key is baseline, which is right for
  // the GraphQL fields the door already gates and would leave these three open to every employee.
  'hiring_test_file',
  'hiring_test_assignment',
  'hiring_test_submission',
  // The dev/QA row scope, which is a DISCIPLINE axis rather than a resource of its own: it decides which
  // test types a caller sees across candidates, tests, candidate tests and assignments alike. Two
  // resources rather than one because holding both is what "sees everything" means — see
  // server/hiring/src/utils/permissionCheck.ts.
  'hiring_dev_test',
  'hiring_qa_test',
  'hiring_job',
  'slack_channel',
  // The per-client dimension. This exists so the account axis the old system enforces is
  // expressible here at all — see the `scope: 'client'` note below and
  // server/graph/lib/services/permissionsTransition/clientPermissionGate.ts.
  //
  // ONE resource with two graded actions, because the two keys answer one question: what you get on a
  // client you are NOT on the service team of. Being on it is a separate axis and needs no key at all —
  // an assignment alone already yields `full`.
  'unassigned_clients',
  'all_meeting_access',
  // The client-scope bypass on the DOCUMENT LIST, the third of this family. Wider than the per-document read
  // bypass — `finance_client_access.view` is finance alone — because the list branch admits crm.srmanager too.
  'all_document_access',
  'all_contract_process_access',
  // The same client-scope bypass on the INVOICE and ACTION-ITEM lists. Both are their own resource for
  // the reason `all_meeting_access` is: `unassigned_clients.full` is read by nine other guards, and it is
  // also granted to the `portal.superadmin` GROUP, which would satisfy it for a superadmin AFTER a
  // `withoutBypass` guard has refused the platform short-circuit — so a guard that refuses the bypass
  // cannot share a key with one that does not.
  'all_invoice_access',
  'all_action_item_access',
  // The company-wide ARM of the finance client-scope rule, rather than a reach level like
  // `unassigned_clients`. Seven guards on a client's DOCUMENTS and CONTRACT TEMPLATES read "you are on
  // this account's service team OR you are Finance", and the second half is a claim about every client
  // at once. `client_billing.view` is the wrong key for it — that one names a client's invoices and
  // purchase orders, and lending it to a document read is how one key comes to mean three things.
  'finance_client_access',
  'client_revenue',
  'client_billing',
  'client_rate_card',
  // The person-scoped object-type field guards on `Person`. Each names the DATA one of those guards
  // protects, not the legacy role that happens to reach it today, and each exists because no declared
  // key had the holder set the guard tests — see server/graph/lib/services/people/person.ts.
  'person_detail',
  'sick_day',
  'mentorship',
  'collaboration',
  // The directory profile fields — phone, about, internal bio, pronouns, birthday — written for SOMEBODY
  // ELSE. Same reason `person_photo` below exists: the mutation that writes them declares
  // `person.update_self`, which is in the BASELINE, so there is no parent key to fold this into that does not
  // mean "every employee". `person.update_self` keeps meaning "maintain your own record"; this one is the
  // cross-person half. See `Person.updateV3` in server/graph/lib/services/people/person.ts.
  'person_profile',
  // The two remaining `Person.updateV3` tiers, for the same reason `person_profile` above exists and no
  // other: their parent is `person.update_self`, which is in the BASELINE, so deleting their legacy checks
  // without keys of their own would have handed both tiers to every employee. `person_visibility` is
  // the `Hidden` flag that takes somebody out of the directory; `person_group` is the assigned-groups
  // string. The superadmin tier writes both that string and the rollover dates, and those are two
  // different capabilities, so it gates as two.
  'person_visibility',
  'person_group',
  // The admin-dashboard person edit and the email change beside it. Both are `person.update_self` in the SDL,
  // which is BASELINE, and both had their real guard in the resolver — so each needs a key that is its
  // own guard's audience and not the whole company's.
  'person_record',
  'person_email',
  // Somebody else's mentorship, education, prior experience and LinkedIn — read by `Person`'s
  // constructor, written by `Person.updateV3`'s resourcing tier. Two different guards with two
  // different lists, so the read and the write are granted separately; see the definitions below.
  'person_background',
  // Direct per-user permission grants, as against `permission_group.assign`, which is the group
  // membership beside them. `Person.permissionsApply` names a strictly narrower list than
  // `Person.groupsApply` does, so the two halves of that one mutation cannot share a key.
  'person_permission',
  // The two REST surfaces that could not be grouped under an existing key (captain, 2026-08-11). Every
  // other upload/download route names the key its own feature already has — `note.update` covers a
  // note's images, `document.view` covers the document's file — because a subfeature does not need its
  // own permission. These two have no such parent: both would fold into `person.update_self`, which is in
  // the BASELINE, so folding them re-opens the hole of any employee editing any employee.
  'person_photo',
  // The rollover dates decide what a person accrues, and their change log is the record of every such
  // rewrite. Both are superadmin-only today and neither has a parent key that is not `person.update_self` or
  // a PTO read key, each of which was in the baseline.
  'pto_rollover',
  // The administrator arms of the five self-service mutations on `people/index.ts`. Each of those is
  // `<an admin role> OR your manager OR you`, so the FIELD is correctly baseline — everybody maintains
  // their own — and it is the first arm that decides who may do it to SOMEBODY ELSE. `person_expertise`
  // covers certifications and professional skill tags together because both arms name `resourcing.admin`
  // and nothing else, so one key is the same population rather than a grouping; the personal skill tags
  // and the disciplines each name a different pair and so keep their own.
  'person_expertise',
  'person_skill',
  'person_discipline',
  // The CROSS-PERSON timesheet surface, which the `timesheet` resource above cannot name because every
  // one of its keys is in the BASELINE — correctly, since tracking your own hours is self-service and
  // gating it would lock the whole company out of the timecard. So the guards that decide whether you
  // may touch SOMEBODY ELSE's week get their own resource, graded by which of the two audiences each
  // one names: `review` is the manager capability, the other three are administrator-only.
  'team_timesheet',
  // The PTO report is the one report whose audience the code already treats as its own: `reportCreate`'s
  // ptoBalance/ptoList branch narrows in-resolver to exactly this route's list, so `report.export` —
  // the union over every report type — is genuinely wider than this boundary.
  'pto_report',
  // The link family is one feature with four parents, and three of them are read by `crm.base` alone
  // while a PROJECT's links are also read by anyone in Polaris. One key cannot say both, so the project
  // half keeps its own read key and `link.view` narrows to the audience the other three name. The
  // update and delete halves need no such split — every parent's guard names the same pair there.
  'project_link',
  // `Query.reportCreate` is ONE field over seven report families, each with its own audience, so the
  // families that already have a resource take an `export` key on it and only the catch-all needs a
  // resource of its own. `report.export` stays the door — the union of all seven, which is what the
  // field can say — and each branch names the family it actually generates.
  'general_report',
  // The revenue figures: revenue by client on the admin dashboard and revenue by region on `/team/rate`.
  // Both pages have always named `crm.admin | crm.srmanager | portal.superadmin` and never
  // `resourcing.admin`, which the utilisation figures beside them do name.
  'revenue_report',
  // The utilisation hours on `/team/utilization`: company-wide hours by month and billing type, overall and
  // per office. Its own resource because the page's audience, `portal.superadmin | resourcing.admin`, is
  // also `person.generate`'s, and resume profile generation is not the same capability.
  'utilization_report',
  // Which project TYPES a report may include, one resource per type because they are one capability per type:
  // `Project.list`'s reporting branch builds its allowed-type list from three independent checks, and a
  // holder of one must not gain the others. Verbose on purpose — `project.view` is the door on a project,
  // these are the tiers of a report over many.
  'billable_project_report',
  'nonbillable_project_report',
  // Legacy `reporting.historical` (31 active, 2 live templates), which was in the unmapped list and gates
  // one thing: whether INACTIVE engagements appear. `inactive: hasHistoricalReportingPermission && ...`
  // in apps/polaris/src/pages/Engagements. It joins the three report tiers above because it is the same
  // kind of question — which slice of the project report you may see — differing on time rather than on
  // billing type.
  'historical_project_report',
  'internal_project_report',
  // The monthly hours lock is a timetracker operation that happens to live in the billable-hours
  // service. It has never shared an audience with the per-row billable-hours surface — that is
  // `crm.base`, this is `timetracker.admin` — so it cannot share `billable_hours.view`/`.lock`.
  'monthly_hours_lock',
  // An ENTRY in a gift campaign is not the campaign: the gifting team and senior managers maintain
  // entries, while creating a campaign and pulling a contact out of one stay with `crm.admin`.
  'gift_campaign_entry',
  // A certification's notes and details are maintained from the CRM by `crm.base`, while creating,
  // renaming, converting and re-skilling one is a Polaris capability. `certification.edit` covers the
  // Polaris half and keeps the four names the image upload left on it — see `certification_image` below
  // and CATALOGUE_FOLD in guardSitePopulations.test.ts for the accepted 2-person cost of that.
  'certification_detail',
  // The account and engagement RECORDS, as against the light per-field edits. `client.edit` and
  // `project.edit` are doors the whole CRM holds — eight fields declare `project.edit` — while creating
  // an account, rewriting one, creating an engagement, rewriting one and requesting one are each a
  // narrower audience that the door cannot name. Same reason `person_record` exists beside
  // `person.update_self`.
  'client_record',
  'project_record',
  // The opportunity WRITE, `scope: 'client'` like its two siblings and for the same reason: the guard is a
  // role list OR any client access to that one account, which no flat key can state. `opportunity.update` is
  // the field's own root declaration and so cannot also be the client-scoped half of its own guard.
  'opportunity_record',
  // One resource per agent WORKFLOW, because the create path checks the workflow being uploaded rather than
  // the hub: an NDA-only holder must not be able to start an MSA run. `app.agent` is the hub DOOR and
  // cannot narrow to a workflow, so it could not serve this.
  'agent_msa_review',
  'agent_nda_review',
  'agent_deck_generation',
  // Agent Admin (KGP-1506), the one way past `ownsRow` in the agent service. Two resources because they
  // are two questions — acting on somebody else's run, and configuring a workflow — although today one
  // legacy permission, `agent.admin`, answers both.
  'agent_run',
  'agent_workflow_setting',
  // Workspaces, a Polaris section rather than an app, so no `app.*` door. Its three keys carry the names
  // its legacy permissions already have — `workspaces.use`, `.manage`, `.admin` — the one deliberate
  // exception to the two vocabularies sharing no names: Workspaces was built after this vocabulary and
  // named for it (captain, 2026-10-16). No legacy grant crosses on the name: legacy_permission_keys
  // maps none of them and the cutover's Workspaces groups start empty. Inside a workspace a member acts
  // by their role there, which is data, so no key grades Editor against Viewer.
  'workspaces',
  // Two REST routes that admit a narrower audience than the feature they belong to, so each needs a
  // key its feature key cannot be without widening. See the definitions below for the coupling that
  // makes that true of every shared key.
  'certification_image',
  'client_logo',
  // The in-app "report a bug" widget, which posts a description and up to six attachments to the
  // support alias. Its own resource because it has no parent: nothing else in the vocabulary is about
  // raising a support ticket, and the nearest keys — `notification.self`, `app.polaris` — mean
  // something else. Granted to the baseline, which is who may file one today.
  'bug_report',
  // FLEXIBLE WORK LIMITS, and TEMPORARY: the entitlements branch (KGP-1241) replaces them with
  // `entitlement_defaults` and deletes the resource, its three actions and their rows. They exist because
  // this system ships first, and the legacy `travel.limit5` / `travel.wfa5` / `travel.wfa10` /
  // `travel.limit0` they carry come from department templates: with nothing re-materialising the legacy
  // rows on a department change, `Person.wfaRestrictions` has to ask the groups instead. Not an access
  // check — no field declares them, and the action names the cap rather than a verb.
  'work_from_anywhere_limit',
  // `app` is the door to an entire surface, and its ACTION names WHICH surface: `app.mcp`,
  // `app.hiring`. That is why the action list below carries several entries that are not verbs. It
  // mirrors the legacy `app.*` family those keys replace, name for name, so the doors that must
  // outlive the deletion of v1/v2 keep the names every reader already knows them by. A resource per
  // app would instead put the app's name in both dimensions and leave `app` itself meaningless.
  'app',
  // Asset Control (server/asset-manager-backend). Its own service and its own database, reached over REST
  // rather than the graph, so no field in either SDL can declare these — they are guarded in that
  // service's own middleware. Listed near the end, beside the other entry points with no GraphQL surface,
  // because both `PermissionResource` enums must equal this list in the SAME ORDER.
  'asset',
  // `portal` carries exactly one key, `portal.superadmin`, and it is not a resource anybody acts ON —
  // it is the BYPASS. Holding it passes every permission check without the grant being enumerated,
  // which is how the legacy system expressed superadmin and why `debug_tool.run` has no group home.
  // A resource of its own rather than an action on `app`, because it is not a door to a surface: it is
  // the absence of a door everywhere at once. Last, for the same ordering reason as `asset`.
  'portal',
] as const;

export type PermissionResource = (typeof PERMISSION_RESOURCES)[number];

/**
 * The verbs a permission can authorise. Closed vocabulary, backed by a real DB enum.
 *
 * Two entries are deliberately NOT verbs — see `mcp` and `hiring` at the end.
 */
export const PERMISSION_ACTIONS = [
  // read
  'view',
  // Reading a resource's CHANGE HISTORY — the `auditlog` rows for it, which carry the before and after
  // values of every audited write plus who made it. Deliberately a separate action from `view` and
  // graded per resource: the history of a thing is a strictly stronger read than the thing, and one
  // audience wanting people's history is not a reason to hand it permission-group history. See
  // BaseGraphObj.AuditLog in server/graph/lib/baseGraphObj.ts for the guard these keys back.
  'audit',
  // Writes. `edit` is create-and-change as ONE capability, and is what a resource carrying both gets:
  // nobody grants adding a record while withholding changing it, and the split was vocabulary rather
  // than authorization (captain, 2026-08-11). `create` and `update` survive only where a resource has
  // just one of them, so the key still names what the endpoint does. `delete` is never folded in —
  // it is destructive and irreversible, and granting editing without it is a real choice people make.
  //
  // ONE EXCEPTION, and it is about SCOPE rather than vocabulary: `client_record` and `project_record`
  // each keep a separate `create` and `update` because their `update` is `scope: 'client'` and their
  // `create` is not. A folded `edit` can only carry one scope, so folding them would either put
  // creating an account behind per-account access nobody holds for an account that does not exist yet,
  // or drop the per-account narrowing from the rewrite.
  'edit',
  'create',
  'update',
  'delete',
  // Reading AND writing one thing as one capability, for a resource whose audience to see it is the
  // audience to change it. `person_background.manage` is the first: the two resourcing notes are a
  // manager's working notes about a person, and an editor who could write them without reading them
  // blanked them on every save.
  'manage',
  // approval workflow
  'submit',
  'approve',
  'reject',
  'review',
  'triage',
  // Recording a QUALITY JUDGEMENT on something a person or a machine produced, as opposed to approving
  // it through a workflow. Separate from `update` because the thing rated is not the thing changed: the
  // rating is its own row, and on an agent run it also leaves the portal for Langfuse.
  'rate',
  // period control
  'lock',
  'unlock',
  'finalize',
  // execution
  'run',
  // A REHEARSAL of something that would otherwise act, returning the decisions it would have made and
  // writing nothing. Its own action rather than `run`, because the audience allowed to rehearse an
  // operation is not the audience allowed to perform it, and rather than `view`, because it takes
  // arguments and computes an answer instead of reading one.
  'dry_run',
  'send',
  // A single deliberately UNGUARDED execution, for end-to-end delivery verification: it acts for real,
  // on one target, past the gates that decide whether the operation should happen at all. Distinct from
  // `send` and `run` because naming it after the ordinary verb is what makes such a key read as
  // permission to perform the operation, when what it grants is permission to bypass its preconditions.
  'test',
  'generate',
  'reset',
  // membership
  'assign',
  // Acting on a row PAST the constraint that normally protects it — an administrative bypass, not the
  // ordinary verb. `review.override` is the first: Employee Services may edit or delete a review the
  // reviewee's own manager may not, because a promotion or a raise is not the manager's to change. Graded
  // separately from `edit` and `delete` on purpose, since the audience that may override is never the
  // audience that may act normally.
  'override',
  // Being let into a section and acting there by your role in each of its records, where the records
  // themselves decide who is an Editor. `workspaces.use` is the only key on it, named as Workspaces'
  // legacy permission already was.
  'use',
  // Self-service. Names a surface that acts only on the caller's OWN rows, where the resolver is what
  // scopes the data and the key only decides whether the surface is available at all. Not a general
  // escape hatch: use it only where the resolver CANNOT return another person's row.
  'self',
  // `update` narrowed to the caller's own row, for a resource whose writes to anybody else's rows each
  // have a key of their own. `person.update_self` is the one: `updatePersonV3` declares those keys and
  // the manager arm beside it, so this key alone writes only your own record.
  'update_self',
  // egress
  'export',
  // Client REACH TIERS on the `unassigned_clients` resource, and the only graded pair here: `full` and
  // `limited` are the two non-`none` values of `ClientAccessLevel`, so the action says WHICH level you
  // get on a client you are not assigned to. Not verbs, like the doors below — and not read tiers
  // either: `limited` admits writes (`invoiceUpsert`, `invoiceApprove`, `polarisDocumentUpload`,
  // `upsertGiftCampaignEntry`, `purchaseOrderUpsert` all accept it on their per-account arm).
  'full',
  'limited',
  // Application doors. These name a SURFACE, not a verb: the `app` resource IS the door, so what varies
  // between them is WHICH door, and there is no third dimension to say it in. They are the ONLY actions
  // naming a surface, and each `app.*` key they form gates exactly the door its identically-named legacy
  // permission in permissionList.ts gated. Do not number the doors in prose here; an ordinal goes
  // stale with the next addition.
  //
  // Each one exists because its app's own read key is in the BASELINE — reading who is visiting, or your own
  // time off, is self-service — so a guard asking "do you have this app at all" had nothing narrower to name.
  'mcp',
  'hiring',
  // The Agent Hub door, and the one app door with NO identically-named legacy permission behind it —
  // which is why it is described separately from the two above. The hub has never had an `app.*`
  // permission: it was LaunchDarkly targeting, and is now "holds any agent workflow permission"
  // (one entry per workflow in AGENT_WORKFLOWS). So this key's audience is that LIST rather than
  // a single matching name, and the two-systems-share-no-vocabulary rule is untouched: `app.agent` is
  // not a legacy permission name.
  'agent',
  // The Polaris door, and another of the names shared with permissionList.ts. Query.comments is the one field
  // whose gate is the app rather than the thing it reads — a compliance-workbook comment is readable by
  // anyone in Polaris, every other object type is re-checked against `comment.view` in the resolver — so
  // the audience it needs is exactly legacy `app.polaris` holders and nothing narrower expresses that.
  'polaris',
  // The Calendar door and the Vault door — two more of the shared names, and the two doors that had no
  // key at all until the cutover audit found them. Every other legacy
  // `app.*` name either has a key of this family or maps to the capability keys behind its app; these
  // two reached 656 and 642 active people through a legacy name with no successor, so deleting v1/v2
  // would have taken Calendar and Vault away from the whole company with nothing to convert to.
  //
  // Neither app has ANY server-side gate to hang a narrower key on — `calendar.view` and
  // `calendar_event.view` are baseline keys, and the vault's own reads are baseline too — so the door
  // is the only question either one asks, exactly as for `app.polaris`.
  'calendar',
  'vault',
  // The My Team door and the Asset Tracker door — the last two legacy `app.*` names that gated a whole
  // app with no key to convert to. `app.myteam` reaches 108 active people and `app.assetcontrol` 29, and
  // both sit on a `PortalContext permissions=` prop, which renders `NoPermission` in place of the app
  // rather than hiding a link. Neither app has a narrower server-side gate to hang a key on: My Team's
  // reads are `review.view`, held by a wider audience than the door, and Asset
  // Control is a separate REST service whose own keys (`asset.edit`, `asset.view`) grade its ADMIN
  // surface, not entry to the tracker.
  //
  // `app.myteam` is the one door of this family that must NOT be reused as a relationship. It stood as a
  // disjunct of `Person.isSubordinateOf` in fourteen guards and was taken out of all of them; it says
  // only that the person can open the app.
  'myteam',
  'assetcontrol',
  // THE DIRECTORY AND HANDBOOK DOORS, and the only two doors deliberately granted to the BASELINE. Both
  // legacy names reach 656 of 657, so the one person they exclude is grant drift rather than policy —
  // and the cutover deleted both checks for exactly that reason. They come back as real doors because
  // the ability to keep an employee out of the directory or the handbook is a thing the business may
  // want and cannot express without a key (captain, 2026-09-09); baseline is how it costs nobody today.
  //
  // The BACKEND half is not wired and would be a no-op while these are baseline: both apps read shared
  // fields (`peopleSimple`, `peopleV2`, `handbookDoc`) that Polaris, PTO and the calendar read too, so
  // there is no field to hang an app door on. Narrowing either key needs a per-app door first, in the
  // shape `app.mcp` and `app.hiring` already have.
  'directory',
  'handbook',
  // THE LINK DOOR. Unlike every other door here it gates a NAV LINK and nothing else, because what it
  // points at is not ours: `app.it` is the Jira service desk, reachable by anyone who types the URL, with
  // access controlled by Atlassian. That makes it decoration in the security sense and useful anyway — it
  // stops the portal advertising a destination the reader has no account for. Stated plainly so nobody
  // later reads it as a gate.
  //
  // The Knowledge Centre link beside it has no door: it is drawn for the departments with a Confluence
  // space of their own, which is who its door was granted to (portalNavUtils.ts).
  'it',
  // The Project Tracker door. It was the ONE legacy `app.*` name still gating something that the cutover
  // gave no key: its two tracker routes were pointed at `timesheet.view` / `.edit`, which are BASELINE,
  // so the nav gate that hid the app from 86 people stopped hiding it from anybody. That was a recorded
  // decision — "the Project Tracker has no `PortalContext` door at all, so the nav gate was the only
  // thing hiding an app already open to everyone" — and it is reversed here (captain, 2026-09-09): the
  // cutover is not the place to widen an app's audience, whatever the merits of doing so later.
  'timetracker',
  // The ChatKGPT door. Its case is the family's shape with one twist: the door was not keyless, it was
  // pointed at the WRONG key — the retired OpenAI session service's, whose fields and keys are gone; the
  // live app reads `llm_conversation.*`. `llm_conversation.self` cannot replace it either: that key is
  // BASELINE, so it draws the app for everybody. Legacy `app.chatkgpt` is the audience, so the door
  // takes its name.
  'chatkgpt',
  // The Licence Tracker door (bloodhound). Its root fields each declare their own `license*` key, and
  // this is the app's entry. Granted to NO group, like every licence key: the captain turned the app
  // back on with the full vocabulary rather than leaving it disabled (2026-09-25), and assigning it is
  // a later decision.
  'licensetracker',
  // The Planner door. Legacy `app.planner` reaches 113 active people and gates a whole Polaris surface —
  // its layout, its four routes and its nav entry — and NOT ONE of the five keys it maps to is exclusive
  // to it. The nearest by audience is `collaboration.view` (139), which is when two employees last
  // worked together, not the Planner. A surface with an audience nothing else expresses is what this family is for.
  'planner',
  // The Admin app door. `apps/admin` is a `PortalContext permissions={['portal.superadmin','portal.admin']}`
  // door and legacy `portal.admin` has no key — `portal` carries the bypass and nothing else. Seven of the
  // keys it maps to land on exactly the door's derived audience of 18 (`entitlement.view`,
  // `person_detail.view`, `person.audit`, `person.send`, `employment_event.view`,
  // `notification_setting.view`, `permission_group.view`), and every one of them grades ONE panel inside
  // the app. Entry is its own question, so it gets its own key rather than borrowing whichever panel
  // happens to share an audience today.
  'admin',
  // The work-from-anywhere app door. `work_from_anywhere_request.view` is in the BASELINE — every employee
  // reads who is visiting — so the arms that ask "do you have this app at all" had no key but the whole
  // company's.
  //
  // The one app door whose name is NOT its legacy permission's. Legacy `app.travel` gates the same door,
  // but "travel" is the legacy product name for what is now work from anywhere, so the door follows the
  // rest of this family instead of its own legacy twin. The app is still titled Travel in the UI; that
  // rename is tracked separately.
  'work_from_anywhere',
  // The PTO app door, another shared name. It is also the key on reading your own time off: an
  // allowance above zero, or the PTO and Flexible Work administration arm.
  'pto',
  // The three Flexible Work caps on `work_from_anywhere_limit` — see that resource. Named for what
  // `Person.wfaRestrictions` does with them, which is not always what the legacy name says: `travel.wfa5`
  // has always allowed ten days.
  'five_days',
  'ten_days',
  'no_days',
  // The bypass, and the last shared name — for the same reason as the app
  // doors above and a stronger one: this key IS the successor to legacy `portal.superadmin`, held by
  // exactly its holders, and the whole point of it is that the legacy check can be deleted. A different
  // name would mean two names for one authority during the only window where confusing them locks
  // somebody out, and `SUPERADMIN_BYPASS_KEY` in permissionGate.ts derives itself from the legacy
  // constant so the identity is a compile-time fact. Not a verb, like the doors above.
  'superadmin',
] as const;

export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

type PermissionDefinition = {
  resource: PermissionResource;
  action: PermissionAction;
  displayName: string;
  description: string;
  /**
   * `'client'` marks a permission whose check TAKES A CLIENT ACCOUNT ID, because the old system
   * enforces it per account and this system has to say the same thing.
   *
   * Such a key is satisfied two ways, exactly as the old disjunction is: a company-wide grant through
   * a group, OR `full` client access to the one account being asked about. So it must never be handed
   * to a check that has no account — that reads the company-wide half alone and widens access to every
   * client.
   *
   * `@requiresPermission` is such a check, so a root field may declare one of these ONLY alongside
   * `orClientAccess: true`, which makes the gate defer to the resolver that does have the account.
   * fieldPermissions.test.ts asserts both halves: no client-scoped declaration without that arm, and
   * the exact set of fields carrying one.
   *
   * Absent = ordinary company-wide permission, which is every other entry here.
   */
  scope?: 'client';
};

export const permissionDefinitions = [
  // ---- Time, absence and travel ----
  {
    resource: 'timesheet',
    action: 'view',
    displayName: 'View Timesheets',
    description: 'Read timesheets; which ones is still scoped by the resolver',
  },
  {
    // THE WEEKLY DISPATCH ITSELF IS NOT HERE. It is a scheduled job (jobScheduler/jobsv2), so nobody
    // calls it and no key gates it; all three of these are the tooling AROUND it, which is why none of
    // them is named after sending a reminder to the company.
    resource: 'timesheet_reminder',
    action: 'view',
    displayName: 'View Timesheet Reminder History',
    description: 'Read the log of weekly timesheet reminders already sent, and who received each one',
  },
  {
    resource: 'timesheet_reminder',
    action: 'dry_run',
    displayName: 'Simulate Timesheet Reminders',
    description: 'Rehearse the dispatcher at a chosen time to see who would be reminded and who is skipped',
  },
  {
    resource: 'timesheet_reminder',
    action: 'test',
    displayName: 'Send a Test Timesheet Reminder',
    description: 'Send one reminder to one person now, past the due-time gate, eligibility and the ledger',
  },
  {
    resource: 'temp_project',
    action: 'view',
    displayName: 'View Temporary Projects',
    description: 'Read temporary projects',
  },
  {
    resource: 'temp_project',
    action: 'edit',
    displayName: 'Edit Temporary Projects',
    description: 'Create and change temporary projects',
  },
  {
    resource: 'temp_project',
    action: 'delete',
    displayName: 'Delete Temporary Projects',
    description: 'Remove temporary projects',
  },
  {
    resource: 'temp_project',
    action: 'assign',
    displayName: 'Assign Temporary Projects',
    description: 'Assign temporary projects',
  },
  {
    resource: 'pto_request',
    action: 'approve',
    displayName: 'Approve PTO Requests',
    description: 'Approve or reject a PTO request',
  },
  {
    resource: 'pto_request',
    action: 'triage',
    displayName: 'Triage PTO Requests',
    description: 'Triage pto requests',
  },
  {
    resource: 'pto_calendar',
    action: 'view',
    displayName: 'View the PTO Calendar',
    description: "Read anybody's approved time off: the PTO calendar page",
  },
  {
    resource: 'pto_calendar',
    action: 'limited',
    displayName: 'View the PTO Calendar for One Office',
    description: "The city-scoped PTO calendar tier: the page, narrowed to the holder's own office",
  },
  {
    resource: 'pto_day',
    action: 'view',
    displayName: 'View Office PTO Days',
    description: 'Read office pto days',
  },
  {
    resource: 'pto_day',
    action: 'create',
    displayName: 'Create Office PTO Days',
    description: 'Add new office pto days',
  },
  {
    resource: 'pto_day',
    action: 'delete',
    displayName: 'Delete Office PTO Days',
    description: 'Remove office pto days',
  },
  {
    resource: 'work_from_anywhere_request',
    action: 'view',
    displayName: 'View Travel Requests',
    description: 'Read travel requests; which ones is still scoped by the resolver',
  },
  {
    // The company-wide bypass on the document UPSERT, and one name wider than the read path's
    // `finance_client_access.view`: writing admits `crm.admin` as well as `crm.finance`, reading does not.
    resource: 'document',
    action: 'override',
    displayName: 'Change Any Clients Documents',
    description: 'Upload or change a document for a client you are not on the service team of',
  },
  {
    // The two cross-person READ tiers, and they are two because their audiences are: unapproved requests are
    // visible to travel admins, the travel viewer role and people managers, while a travel MODERATOR reads
    // the list on its own terms.
    resource: 'team_work_from_anywhere_request',
    action: 'view',
    displayName: "See Another Person's Travel Requests",
    description: 'Read travel requests belonging to other people, including unapproved ones',
  },
  {
    resource: 'team_work_from_anywhere_request',
    action: 'triage',
    displayName: 'Moderate Travel Requests',
    description: "Read the travel request list as a moderator, whoever's requests they are",
  },
  {
    resource: 'team_work_from_anywhere_request',
    action: 'approve',
    displayName: 'Approve Another Persons Travel Request',
    description: 'Approve a travel request somebody else submitted',
  },
  {
    resource: 'team_work_from_anywhere_request',
    action: 'reject',
    displayName: 'Cancel or Decline Another Persons Travel Request',
    description: 'Cancel or decline a travel request somebody else submitted',
  },
  {
    // WHO A REQUEST IS ADDRESSED TO, which is a narrower thing than who may act on one. The approver
    // walk climbs the requester's management chain looking for the nearest holder of this
    // (server/graph/lib/services/travel/wfaApprover.ts), so the holder is the routing TARGET rather
    // than the actor. Granted to `travel.admin` alone: `.approve` also reaches `travel.moderator`, and
    // routing to the nearest of those instead would move 99 of 654 people's approver.
    resource: 'team_work_from_anywhere_request',
    action: 'assign',
    displayName: 'Receive Work From Anywhere Requests for Approval',
    description: 'Be named the approving operations contact on work from anywhere requests raised below you',
  },
  {
    resource: 'team_work_from_anywhere_request',
    action: 'review',
    displayName: 'Manage Your Reports Travel Requests',
    description: 'See and act on the travel requests of people you manage',
  },
  {
    resource: 'team_work_from_anywhere_request',
    action: 'create',
    displayName: "Raise Somebody Else's Travel Request",
    description: 'Create a travel request on behalf of another employee',
  },
  {
    // THE OPS TIER INSIDE `approve`, not a second way of spelling it. `team_work_from_anywhere_request.approve` is
    // whether a caller may approve somebody else's request at all; this is whether their approval LANDS
    // as the final one — `ADMIN_APPROVED` with the caller recorded as the operations approver — instead
    // of `MANAGER_APPROVED` still waiting on ops. Two audiences, so two keys: approving is
    // `{travel.admin, travel.moderator}` and finalising is `travel.admin` alone.
    resource: 'team_work_from_anywhere_request',
    action: 'finalize',
    displayName: "Give a Travel Request Its Final Approval",
    description: 'Approve a travel request as operations, which finalises it rather than leaving it for ops',
  },
  {
    // The relationship-scope bypass on the travel LIST. `team_work_from_anywhere_request.view` is the tier that reads
    // other people's requests with the resolver still narrowing them to your own reports; this is what
    // reads EVERY employee's and resolves any manager's team. `TravelRequest.list` uses the two names it
    // is granted to only ever as a disjunction, so this is one capability rather than two.
    resource: 'all_work_from_anywhere_request_access',
    action: 'view',
    displayName: "Read Every Employee's Travel Requests",
    description: "Read every employee's travel requests and any manager's team, without managing them",
  },
  {
    resource: 'work_from_anywhere_request',
    action: 'approve',
    displayName: 'Approve Travel Requests',
    description: 'Approve or reject a travel request',
  },
  {
    resource: 'work_from_anywhere_request',
    action: 'reject',
    displayName: 'Reject Travel Requests',
    description: 'Reject travel requests',
  },
  {
    resource: 'trip',
    action: 'view',
    displayName: 'View Trips',
    description: 'Read trips',
  },
  {
    resource: 'trip',
    action: 'update',
    displayName: 'Edit Trips',
    description: 'Change trips',
  },
  {
    resource: 'trip',
    action: 'delete',
    displayName: 'Delete Trips',
    description: 'Remove trips',
  },
  {
    resource: 'assignment_log',
    action: 'view',
    displayName: 'View Assignment Logs',
    description: 'Read assignment logs',
  },
  {
    resource: 'assignment_log',
    action: 'delete',
    displayName: 'Delete Assignment Logs',
    description: 'Remove assignment logs',
  },
  {
    resource: 'assignment_log',
    action: 'approve',
    displayName: 'Approve Assignment Logs',
    description: 'Approve assignment logs',
  },
  {
    resource: 'assignment_log',
    action: 'run',
    displayName: 'Run Assignment Logs',
    description: 'Trigger execution of assignment logs',
  },
  {
    resource: 'blackout_period',
    action: 'view',
    displayName: 'View Project Blackout Periods',
    description: 'Read project blackout periods',
  },
  {
    resource: 'blackout_period',
    action: 'update',
    displayName: 'Edit Project Blackout Periods',
    description: 'Change project blackout periods',
  },
  {
    resource: 'blackout_period',
    action: 'delete',
    displayName: 'Delete Project Blackout Periods',
    description: 'Remove project blackout periods',
  },

  // ---- People, HR and access control ----
  {
    resource: 'person',
    action: 'view',
    displayName: 'View People',
    description: 'Read the directory record of any employee',
  },
  {
    resource: 'person',
    action: 'update_self',
    displayName: 'Edit Your Own Profile',
    description: 'Change your own directory profile. Changing anybody else\'s takes a person_* key or managing them',
  },
  {
    resource: 'person',
    action: 'send',
    displayName: 'Send People',
    description: 'Send people',
  },
  {
    resource: 'person',
    action: 'generate',
    displayName: 'Generate Employee Profiles',
    description: "Build an employee's profile from an uploaded resume (POST /generateProfileFromResume)",
  },
  {
    resource: 'person',
    action: 'export',
    displayName: "Export a Direct-Reports Report",
    description: 'Generate the directReports report over the people who report to you',
  },
  {
    resource: 'person',
    action: 'audit',
    displayName: "View People's Change History",
    description: "Read the before/after of every audited change to anyone's person record (Person.AuditLog)",
  },
  {
    // One key for the read and the write: whoever may end someone's sessions is who needs to see when they last did.
    resource: 'person_session',
    action: 'manage',
    displayName: 'Sign Out Everywhere',
    description: "See a person's last sign-in, and end every portal session they hold",
  },
  {
    resource: 'employment_event',
    action: 'view',
    displayName: 'View Employment Events',
    description: 'Read employment events',
  },
  {
    resource: 'employment_event',
    action: 'edit',
    displayName: 'Edit Employment Events',
    description: 'Create and change employment events',
  },
  {
    resource: 'employment_event',
    action: 'delete',
    displayName: 'Delete Employment Events',
    description: 'Remove employment events',
  },
  {
    resource: 'offboarding',
    action: 'view',
    displayName: 'View Offboarding Records',
    description: 'Read offboarding records',
  },
  {
    resource: 'offboarding',
    action: 'update',
    displayName: 'Edit Offboarding Records',
    description: 'Change offboarding records',
  },
  {
    resource: 'onboarding',
    action: 'create',
    displayName: 'Create Onboarding',
    description: 'Add new onboarding',
  },
  {
    resource: 'certification',
    action: 'edit',
    displayName: 'Edit Certifications',
    description: 'Create and change certifications',
  },
  {
    resource: 'certification',
    action: 'delete',
    displayName: 'Delete Certifications',
    description: 'Remove certifications',
  },
  {
    resource: 'skill_tag',
    action: 'update',
    displayName: 'Edit Skill Tags',
    description: 'Change skill tags',
  },
  {
    resource: 'skill_tag',
    action: 'delete',
    displayName: 'Delete Skill Tags',
    description: 'Remove skill tags',
  },
  {
    resource: 'work_history',
    action: 'view',
    displayName: 'View Work History',
    description: 'Read work history',
  },
  {
    resource: 'work_history',
    action: 'edit',
    displayName: 'Edit Work History',
    description: 'Create and change work history',
  },
  {
    resource: 'work_history',
    action: 'delete',
    displayName: 'Delete Work History',
    description: 'Remove work history',
  },
  {
    resource: 'work_history',
    action: 'override',
    displayName: "Edit Anyone's Work History",
    description: 'Create, change and remove work history for people who are not you or your reports',
  },
  {
    resource: 'discipline',
    action: 'edit',
    displayName: 'Edit Disciplines',
    description: 'Create and change disciplines, including their display order',
  },
  {
    resource: 'department',
    action: 'update',
    displayName: 'Edit Departments',
    description: 'Change departments, including their display order',
  },
  {
    resource: 'office',
    action: 'update',
    displayName: 'Edit Offices',
    description: 'Change offices, including their display order',
  },
  {
    resource: 'office',
    action: 'audit',
    displayName: 'View Office Change History',
    description: 'Read the change history of an office record (Office.AuditLog)',
  },
  {
    resource: 'job_title',
    action: 'update',
    displayName: 'Edit Job Titles',
    description: 'Change job titles, including their display order',
  },
  {
    resource: 'review',
    action: 'view',
    displayName: 'View Performance Reviews',
    description: 'Read performance reviews',
  },
  {
    resource: 'review',
    action: 'edit',
    displayName: 'Edit Performance Reviews',
    description: 'Create and change performance reviews',
  },
  {
    resource: 'review',
    action: 'delete',
    displayName: 'Delete Performance Reviews',
    description: 'Remove performance reviews',
  },
  {
    resource: 'review',
    action: 'export',
    displayName: 'Export the Review History Report',
    description: 'Generate the reviewHistory report',
  },
  {
    resource: 'review',
    action: 'approve',
    displayName: 'Approve Performance Reviews',
    description: 'Approve performance reviews',
  },
  {
    resource: 'handbook',
    action: 'view',
    displayName: 'View Handbook Documents',
    description: 'Read handbook documents',
  },
  {
    resource: 'handbook',
    action: 'edit',
    displayName: 'Edit Handbook Documents',
    description: 'Create and change handbook documents',
  },
  {
    // `Query.handbookDocs`, `Query.handbookDocsByTitle` and `Query.handbookUpdatesLogs` — the three that
    // read across every office and department, and the update history. Their three siblings read only
    // what is scoped to the caller and carry no check at all, which is why `handbook.view` is baseline
    // and this cannot be a narrowing of it.
    resource: 'all_handbook_access',
    action: 'view',
    displayName: 'Read Every Handbook',
    description: "Every handbook document and its update history, past the office and department scoping",
  },
  {
    resource: 'manager_doc',
    action: 'view',
    displayName: 'View Manager Documents',
    description: 'Read manager documents',
  },
  {
    resource: 'manager_doc',
    action: 'update',
    displayName: 'Edit Manager Documents',
    description: 'Change manager documents, including their display order',
  },
  {
    resource: 'manager_doc',
    action: 'delete',
    displayName: 'Delete Manager Documents',
    description: 'Remove manager documents',
  },
  {
    resource: 'manager_doc',
    action: 'audit',
    displayName: 'View Manager Doc Change History',
    description: 'Read the change history of a manager doc (ManagerDoc.AuditLog)',
  },
  {
    resource: 'powerhour',
    action: 'view',
    displayName: 'View Power Hours',
    description: 'Read power hours',
  },
  {
    resource: 'entitlement',
    action: 'view',
    displayName: 'View Entitlements',
    description: 'Read the entitlement default rules and per-person overrides',
  },
  {
    resource: 'entitlement',
    action: 'update',
    displayName: 'Edit Entitlements',
    description: 'Change the entitlement default rules and per-person overrides',
  },
  {
    resource: 'entitlement',
    action: 'audit',
    displayName: 'View Entitlement Change History',
    description: 'Read who changed an office/department/discipline day-cap default, and from what to what',
  },
  {
    resource: 'permission_group',
    action: 'view',
    displayName: 'View Permission Groups',
    description: 'Read the permission groups and who belongs to them',
  },
  {
    resource: 'permission_group',
    action: 'update',
    displayName: 'Edit Permission Groups',
    description: 'Change what a permission group grants and who belongs to it',
  },
  {
    resource: 'permission_group',
    action: 'delete',
    displayName: 'Delete Permission Groups',
    description: 'Remove permission groups',
  },
  {
    resource: 'permission_group',
    action: 'assign',
    displayName: 'Assign Permission Groups',
    description: 'Assign permission groups',
  },
  {
    resource: 'permission_group',
    action: 'audit',
    displayName: 'View Access Group Change History',
    description: 'Read grant and membership changes for an access group, in either permission system',
  },
  {
    resource: 'team_profile',
    action: 'view',
    displayName: 'View Team Profiles',
    description: 'Read team profiles',
  },
  {
    resource: 'team_profile',
    action: 'update',
    displayName: 'Edit Team Profiles',
    description: 'Change team profiles',
  },

  // ---- Clients, contacts and delivery ----
  {
    resource: 'client',
    action: 'view',
    displayName: 'View Clients',
    description: 'Read client records and their engagements',
  },
  {
    resource: 'client',
    action: 'edit',
    displayName: 'Edit Clients',
    description: 'Create clients and change client records and their engagements',
  },
  {
    resource: 'client',
    action: 'delete',
    displayName: 'Delete Clients',
    description: 'Remove clients',
  },
  {
    resource: 'client',
    action: 'reject',
    displayName: 'Reject Clients',
    description: 'Reject clients',
  },
  {
    resource: 'client_service_team',
    action: 'view',
    displayName: 'View Client Service Teams',
    description: 'Read client service teams',
  },
  {
    resource: 'client_service_team',
    action: 'update',
    displayName: 'Edit Client Service Teams',
    description: 'Change client service teams',
  },
  {
    resource: 'client_service_team',
    action: 'delete',
    displayName: 'Delete Client Service Teams',
    description: 'Remove client service teams',
  },
  {
    resource: 'client_service_map',
    action: 'view',
    displayName: 'View Client Service Maps',
    description: 'Read client service maps',
  },
  {
    resource: 'client_service_map',
    action: 'update',
    displayName: 'Edit Client Service Maps',
    description: 'Change client service maps',
  },
  {
    resource: 'client_service_map',
    action: 'delete',
    displayName: 'Delete Client Service Maps',
    description: 'Remove client service maps',
  },
  {
    resource: 'client_compliance',
    action: 'update',
    displayName: 'Edit Client Compliance Records',
    description: 'Change client compliance records',
  },
  {
    resource: 'compliance_workbook',
    action: 'view',
    displayName: 'View Compliance Workbooks',
    description: 'Read compliance workbooks',
  },
  {
    resource: 'compliance_workbook',
    action: 'edit',
    displayName: 'Edit Compliance Workbooks',
    description: 'Create and change compliance workbooks',
  },
  {
    resource: 'compliance_workbook',
    action: 'review',
    displayName: 'Review Compliance Workbooks',
    description: 'Mark as reviewed compliance workbooks',
  },
  {
    resource: 'client_sales_meeting',
    action: 'view',
    displayName: 'View Client Sales Meetings',
    description: 'Read client sales meetings',
  },
  {
    resource: 'client_sales_meeting',
    action: 'update',
    displayName: 'Edit Client Sales Meetings',
    description: 'Change client sales meetings',
  },
  {
    resource: 'client_sales_meeting',
    action: 'delete',
    displayName: 'Delete Client Sales Meetings',
    description: 'Remove client sales meetings',
  },
  {
    resource: 'meeting',
    action: 'view',
    displayName: 'View Meetings',
    description: 'Read meetings',
  },
  {
    resource: 'meeting',
    action: 'update',
    displayName: 'Edit Meetings',
    description: 'Change meetings',
  },
  {
    resource: 'meeting',
    action: 'delete',
    displayName: 'Delete Meetings',
    description: 'Remove meetings',
  },
  {
    resource: 'contact',
    action: 'view',
    displayName: 'View Contacts',
    description: 'Read contacts',
  },
  {
    resource: 'contact',
    action: 'update',
    displayName: 'Edit Contacts',
    description: 'Change contacts',
  },
  {
    resource: 'contact',
    action: 'export',
    displayName: 'Export Contact Lists',
    description: 'Generate the mailingListByTag and polarisClients reports',
  },
  {
    resource: 'contact',
    action: 'delete',
    displayName: 'Delete Contacts',
    description: 'Remove contacts',
  },
  {
    resource: 'opportunity',
    action: 'view',
    displayName: 'View Opportunities',
    description: 'Read opportunities',
  },
  {
    resource: 'opportunity',
    action: 'update',
    displayName: 'Edit Opportunities',
    description: 'Change opportunities',
  },
  {
    // Flipping an opportunity to APPROVED, which `opportunity.update` cannot say: that key is the write door
    // and reaches all of `crm.base`, where approval is the CRM admins alone.
    resource: 'opportunity',
    action: 'approve',
    displayName: 'Approve an Opportunity',
    description: 'Mark an opportunity approved',
  },
  {
    resource: 'opportunity',
    action: 'delete',
    displayName: 'Delete Opportunities',
    description: 'Remove opportunities',
  },
  {
    resource: 'proposal',
    action: 'view',
    displayName: 'View Proposals',
    description: 'Read proposals',
  },
  {
    resource: 'proposal',
    action: 'update',
    displayName: 'Edit Proposals',
    description: 'Change proposals',
  },
  {
    resource: 'proposal',
    action: 'delete',
    displayName: 'Delete Proposals',
    description: 'Remove proposals',
  },
  {
    resource: 'project',
    action: 'view',
    displayName: 'View Engagements',
    description: 'Read engagement records and their staffing',
  },
  {
    resource: 'project',
    action: 'edit',
    displayName: 'Edit Engagements',
    description: 'Create engagements and change their records and staffing',
  },
  {
    resource: 'project',
    action: 'delete',
    displayName: 'Delete Engagements',
    description: 'Remove engagements',
  },
  {
    resource: 'program',
    action: 'edit',
    displayName: 'Edit Programs',
    description: 'Create and change programs',
  },
  {
    resource: 'program',
    action: 'delete',
    displayName: 'Delete Programs',
    description: 'Remove programs',
  },
  {
    resource: 'program',
    action: 'assign',
    displayName: 'Assign Programs',
    description: 'Assign programs to projects, and remove those assignments',
  },
  {
    resource: 'contract_template',
    action: 'view',
    displayName: 'View Contract Templates',
    description: 'Read contract templates',
  },
  {
    // The Contract Pipeline arrived after the permission migration, so it never had keys of its own —
    // it gated on `crm.base` plus client access. These two state that base arm, and the client half
    // stays where every other client-scoped surface keeps it: the access level, not the key.
    resource: 'contract_process',
    action: 'view',
    displayName: 'View Contract Processes',
    description: "Read a client's contract processes and their comment feed",
  },
  {
    resource: 'contract_process',
    action: 'update',
    displayName: 'Edit Contract Processes',
    description: 'Create and change contract processes, including the inline edits on the pipeline',
  },
  {
    resource: 'contract_process',
    action: 'delete',
    displayName: 'Delete Contract Processes',
    description: 'Soft-delete contract processes, on any client',
  },
  {
    // Narrower than `update` on purpose: only Contract Governance may set or clear Executed (Processed).
    resource: 'contract_process',
    action: 'finalize',
    displayName: 'Mark Contract Processes Executed',
    description: 'Move a contract process into or out of Executed (Processed)',
  },
  {
    resource: 'service_group',
    action: 'view',
    displayName: 'View Service Groups',
    description: 'Read service groups',
  },
  {
    resource: 'service_group',
    action: 'update',
    displayName: 'Edit Service Groups',
    description: 'Change service groups',
  },
  {
    resource: 'service_group',
    action: 'delete',
    displayName: 'Delete Service Groups',
    description: 'Remove service groups',
  },
  {
    // The city gazetteer itself is @noPermissionRequired — public geographic reference data. What this
    // key still gates is `citySearchLogs`, which is who searched for which city and when.
    resource: 'city',
    action: 'view',
    displayName: 'View City Search Logs',
    description: 'Read the log of which people searched for which cities',
  },
  {
    resource: 'gift_campaign',
    action: 'view',
    displayName: 'View Gift Campaigns',
    description: 'Read gift campaigns',
  },
  {
    resource: 'gift_campaign',
    action: 'update',
    displayName: 'Edit Gift Campaigns',
    description: 'Change gift campaigns',
  },
  {
    resource: 'gift_campaign',
    action: 'delete',
    displayName: 'Delete Gift Campaigns',
    description: 'Remove gift campaigns',
  },
  {
    resource: 'action_item',
    action: 'view',
    displayName: 'View Action Items',
    description: 'Read action items',
  },
  {
    resource: 'action_item',
    action: 'update',
    displayName: 'Edit Action Items',
    description: 'Change action items',
  },
  {
    resource: 'action_item',
    action: 'delete',
    displayName: 'Delete Action Items',
    description: 'Remove action items',
  },
  {
    resource: 'comment',
    action: 'view',
    displayName: 'View Comments',
    description: 'Read comments',
  },
  {
    resource: 'comment',
    action: 'update',
    displayName: 'Edit Comments',
    description: 'Change comments',
  },
  {
    // Deleting a comment SOMEBODY ELSE wrote. `comment.delete` is the door and reaches all of `crm.base`;
    // this is the moderation override inside the resolver, beside `ownsRow`.
    resource: 'comment',
    action: 'override',
    displayName: "Delete Another Person's Comment",
    description: 'Delete a comment you did not write',
  },
  {
    resource: 'comment',
    action: 'delete',
    displayName: 'Delete Comments',
    description: 'Remove comments',
  },
  {
    resource: 'note',
    action: 'view',
    displayName: 'View Notes',
    description: 'Read notes',
  },
  {
    resource: 'note',
    action: 'update',
    displayName: 'Edit Notes',
    description: 'Change notes',
  },
  {
    resource: 'note',
    action: 'delete',
    displayName: 'Delete Notes',
    description: 'Remove notes',
  },
  {
    resource: 'global_note',
    action: 'view',
    displayName: 'View Global Notes',
    description: 'Read global notes',
  },
  {
    resource: 'global_note',
    action: 'update',
    displayName: 'Edit Global Notes',
    description: 'Change global notes',
  },
  {
    resource: 'global_note',
    action: 'delete',
    displayName: 'Delete Global Notes',
    description: 'Remove global notes',
  },
  {
    resource: 'document',
    action: 'view',
    displayName: 'View Documents',
    description: 'Read documents',
  },
  {
    resource: 'document',
    action: 'update',
    displayName: 'Edit Documents',
    description: 'Change documents',
  },
  {
    resource: 'document',
    action: 'delete',
    displayName: 'Delete Documents',
    description: 'Remove documents',
  },
  {
    resource: 'file',
    action: 'view',
    displayName: 'View Files',
    description: 'Read files',
  },
  {
    resource: 'file',
    action: 'update',
    displayName: 'Edit Files',
    description: 'Change files',
  },
  {
    resource: 'file',
    action: 'delete',
    displayName: 'Delete Files',
    description: 'Remove files',
  },
  {
    resource: 'link',
    action: 'view',
    displayName: 'View Links',
    description: 'Read links',
  },
  {
    resource: 'link',
    action: 'update',
    displayName: 'Edit Links',
    description: 'Change links',
  },
  {
    resource: 'link',
    action: 'delete',
    displayName: 'Delete Links',
    description: 'Remove links',
  },
  {
    resource: 'portal_tag',
    action: 'view',
    displayName: 'View Portal Tags',
    description: 'Read portal tags',
  },
  {
    resource: 'portal_tag',
    action: 'edit',
    displayName: 'Edit Portal Tags',
    description: 'Create and change portal tags',
  },
  {
    resource: 'portal_tag',
    action: 'delete',
    displayName: 'Delete Portal Tags',
    description: 'Remove portal tags',
  },

  // ---- Finance ----
  {
    resource: 'invoice',
    action: 'view',
    displayName: 'View Invoices',
    description: 'Read invoices',
  },
  {
    resource: 'invoice',
    action: 'update',
    displayName: 'Edit Invoices',
    description: 'Change invoices',
  },
  {
    resource: 'invoice',
    action: 'delete',
    displayName: 'Delete Invoices',
    description: 'Remove invoices',
  },
  {
    resource: 'invoice',
    action: 'approve',
    displayName: 'Approve Invoices',
    description: 'Approve invoices, and withdraw that approval',
  },
  {
    resource: 'purchase_order',
    action: 'view',
    displayName: 'View Purchase Orders',
    description: 'Read purchase orders',
  },
  {
    resource: 'purchase_order',
    action: 'update',
    displayName: 'Edit Purchase Orders',
    description: 'Change purchase orders',
  },
  {
    resource: 'purchase_order',
    action: 'delete',
    displayName: 'Delete Purchase Orders',
    description: 'Remove purchase orders',
  },
  {
    resource: 'purchase_order',
    action: 'approve',
    displayName: 'Approve Purchase Orders',
    description: 'Approve purchase orders, and withdraw that approval',
  },
  {
    resource: 'rate_card',
    action: 'view',
    displayName: 'View Rate Cards',
    description: 'Read rate cards',
  },
  {
    resource: 'rate_card',
    action: 'update',
    displayName: 'Edit Rate Cards',
    description: 'Change rate cards',
  },
  {
    resource: 'rate_card',
    action: 'delete',
    displayName: 'Delete Rate Cards',
    description: 'Remove rate cards',
  },
  {
    resource: 'revenue_recognition',
    action: 'view',
    displayName: 'View Revenue Recognition',
    description: 'Read revenue recognition',
  },
  {
    resource: 'revenue_recognition',
    action: 'update',
    displayName: 'Edit Revenue Recognition',
    description: 'Change revenue recognition',
  },
  {
    resource: 'locked_revenue',
    action: 'view',
    displayName: 'View Locked Revenue Months',
    description: 'Read locked revenue months',
  },
  {
    resource: 'locked_revenue',
    action: 'delete',
    displayName: 'Delete Locked Revenue Months',
    description: 'Remove locked revenue months',
  },
  {
    resource: 'locked_revenue',
    action: 'lock',
    displayName: 'Lock Locked Revenue Months',
    description: 'Lock locked revenue months',
  },
  {
    resource: 'locked_revenue',
    action: 'unlock',
    displayName: 'Unlock Locked Revenue Months',
    description: 'Unlock locked revenue months',
  },
  {
    resource: 'billable_hours',
    action: 'view',
    displayName: 'View Billable Hours',
    description: 'Read billable hours',
  },
  {
    resource: 'billable_hours',
    action: 'update',
    displayName: 'Edit Billable Hours',
    description: 'Change billable hours',
  },
  {
    resource: 'billable_hours',
    action: 'approve',
    displayName: 'Approve Billable Hours',
    description: 'Approve billable hours, and withdraw that approval',
  },
  {
    resource: 'billable_hours',
    action: 'lock',
    displayName: 'Lock Billable Hours',
    description: 'Lock billable hours',
  },
  {
    resource: 'billable_hours',
    action: 'unlock',
    displayName: 'Unlock Billable Hours',
    description: 'Unlock billable hours',
  },
  {
    resource: 'forecast',
    action: 'export',
    displayName: 'Export the Forecast Report',
    description: 'Generate the forecast report, which carries client revenue figures',
  },
  {
    resource: 'forecast',
    action: 'update',
    displayName: 'Edit Forecast Data',
    description: 'Change forecast data',
  },
  {
    resource: 'business_days',
    action: 'update',
    displayName: 'Edit Business Days',
    description: 'Change business days',
  },

  // ---- Reporting ----
  {
    resource: 'report',
    action: 'export',
    displayName: 'Export Reports',
    description: 'Download report output as a file',
  },
  {
    resource: 'trend',
    action: 'view',
    displayName: 'View Trend Data',
    description: 'Read trend data',
  },
  {
    resource: 'polaris_metric',
    action: 'view',
    displayName: 'View Polaris Metrics',
    description: 'Read polaris metrics',
  },
  {
    resource: 'polaris_metric',
    action: 'export',
    displayName: 'Export the Polaris Reports',
    description: 'Generate the daysIdleByDept and KGLibsCompliance reports',
  },
  {
    resource: 'client_record',
    action: 'create',
    displayName: 'Create a Client Account',
    description: 'Add a new client account to the portal',
  },
  {
    resource: 'client_record',
    action: 'update',
    displayName: "Edit a Client's Record",
    description: "Change a client account's own fields — its name, sales entity, currency and status",
    scope: 'client',
  },
  {
    resource: 'opportunity_record',
    action: 'update',
    displayName: 'Edit an Opportunity',
    description: "Create or change an opportunity on a client you have access to",
    scope: 'client',
  },
  {
    resource: 'person_pto_override',
    action: 'update',
    displayName: "Override a Person's PTO Entitlement",
    description: "Set a PTO entitlement override on one person, outside the entitlement rules",
  },
  {
    resource: 'project_record',
    action: 'create',
    displayName: 'Create an Engagement',
    description: 'Add a new engagement to a client account',
  },
  {
    resource: 'project_record',
    action: 'update',
    displayName: "Edit an Engagement's Record",
    description: "Change an engagement's own fields, as against the notes and description anyone in the CRM may edit",
    scope: 'client',
  },
  {
    resource: 'project_record',
    action: 'submit',
    displayName: 'Request an Engagement',
    description: 'Submit an engagement request for approval, without creating the engagement itself',
  },
  {
    resource: 'certification_detail',
    action: 'update',
    displayName: "Edit a Certification's Notes and Details",
    description: 'Add a note to a certification and change its descriptive fields',
  },
  {
    resource: 'monthly_hours_lock',
    action: 'view',
    displayName: 'View the Monthly Hours Lock',
    description: 'Read which month the billable-hours lock has last been run for',
  },
  {
    resource: 'monthly_hours_lock',
    action: 'lock',
    displayName: 'Run the Monthly Hours Lock',
    description: "Lock a month's billable hours across the portal",
  },
  {
    resource: 'gift_campaign_entry',
    action: 'update',
    displayName: 'Maintain Gift Campaign Entries',
    description: "Add or change a contact's entry in a gift campaign",
  },
  {
    resource: 'general_report',
    action: 'export',
    displayName: 'Export a General Portal Report',
    description: 'Generate any report type with no audience of its own — the reportCreate catch-all',
  },
  {
    resource: 'opportunity_stat',
    action: 'view',
    displayName: 'View Opportunity Statistics',
    description: 'Read opportunity statistics',
  },
  {
    resource: 'tva_report',
    action: 'view',
    displayName: 'View TVA Reports',
    description: 'Read tva reports',
  },
  {
    resource: 'tva_report',
    action: 'update',
    displayName: 'Edit TVA Reports',
    description: 'Change tva reports',
  },
  {
    resource: 'tva_report',
    action: 'review',
    displayName: 'Review TVA Reports',
    description: 'Mark as reviewed tva reports',
  },

  // ---- Platform, admin and integrations ----
  {
    resource: 'email_template',
    action: 'view',
    displayName: 'View Email Templates',
    description: 'Read email templates',
  },
  {
    resource: 'email_template',
    action: 'edit',
    displayName: 'Edit Email Templates',
    description: 'Create and change email templates',
  },
  {
    resource: 'email_template',
    action: 'delete',
    displayName: 'Delete Email Templates',
    description: 'Remove email templates',
  },
  {
    resource: 'email_template',
    action: 'send',
    displayName: 'Send Email Templates',
    description: 'Send email templates',
  },
  {
    resource: 'email_log',
    action: 'view',
    displayName: 'View Email Logs',
    description: 'Read email logs',
  },
  {
    resource: 'job',
    action: 'view',
    displayName: 'View Scheduled Jobs',
    description: 'Read scheduled jobs',
  },
  {
    resource: 'job',
    action: 'run',
    displayName: 'Run Scheduled Jobs',
    description: 'Trigger execution of scheduled jobs',
  },
  {
    // One key for the whole of a person's OWN notification centre — listing, marking read, archiving,
    // flagging important. It is one capability, not four, because every one of them is the same act on
    // the same rows: `KGNotification.list` is scoped to `UserID = curUser.id`, so the permission decides
    // whether the notification centre works for you at all, never whose notifications you see.
    //
    // It exists because the alternative was `notification.send`-shaped: a permission modelling a user
    // action that only the SYSTEM performs. Sending is what seven scheduled jobs, the vault email path
    // and one Polaris route do through the internal `sendNotification` helper, none of which holds a
    // permission — the GraphQL mutation that made it look like a user action has been removed.
    resource: 'notification',
    action: 'self',
    displayName: 'Use Your Notification Centre',
    description: 'Read, dismiss and act on your own notifications',
  },
  {
    resource: 'notification',
    action: 'delete',
    displayName: 'Delete Notifications',
    description: 'Remove notifications',
  },
  {
    // The admin "send a test notification" button, and nothing else. Real notifications are sent by the
    // system, not by a person — see `notification.self`.
    resource: 'notification',
    action: 'send',
    displayName: 'Send Test Notifications',
    description: 'Fire a test notification to the email whitelist from the admin screen',
  },
  {
    resource: 'notification_setting',
    action: 'view',
    displayName: 'View Notification Settings',
    description: 'Read notification settings',
  },
  {
    resource: 'notification_setting',
    action: 'update',
    displayName: 'Edit Notification Settings',
    description: 'Change notification settings',
  },
  {
    resource: 'slack_message',
    action: 'view',
    displayName: 'View Slack Messages',
    description: 'Read slack messages',
  },
  {
    resource: 'slack_message',
    action: 'send',
    displayName: 'Send Slack Messages',
    description: 'Send slack messages',
  },
  {
    resource: 'sql_script',
    action: 'view',
    displayName: 'View SQL Scripts',
    description: 'Read sql scripts',
  },
  {
    resource: 'sql_script',
    action: 'update',
    displayName: 'Edit SQL Scripts',
    description: 'Change sql scripts',
  },
  {
    resource: 'cache',
    action: 'view',
    displayName: 'View Caches',
    description: 'Read caches',
  },
  {
    resource: 'cache',
    action: 'reset',
    displayName: 'Reset Caches',
    description: 'Clear or rebuild caches',
  },
  {
    // `Query.tablesInfo`, which is superadmin-only in the resolver. Its `environment` sibling reads only
    // the environment name for the same System page, and takes that page's `job.view`.
    resource: 'database_table',
    action: 'view',
    displayName: 'View Database Tables',
    description: 'Every table in the portal database, with its row count',
  },
  {
    // The three admin-dashboard totals, superadmin-only in the resolver, so they cannot share a key with
    // the `adminGroupedHours*` reads, each of which takes its own Polaris page's door.
    resource: 'admin_metric',
    action: 'view',
    displayName: 'View Portal Totals',
    description: 'Portal-wide project, employee and timesheet totals, and hours by billing and project type',
  },
  {
    // The vault is one self-service capability. All seven of its fields — the inbox, your uploads, a
    // single file, the activity log, the timed download URL, the expiry change and the delete — resolve
    // against the caller and throw `ForbiddenError` on another person's file. There is no admin view:
    // nothing in the service enumerates anyone else's vault, so the key never decides WHICH files you
    // see, only whether the vault opens at all.
    //
    // Deleting is folded in here rather than kept separate, which is the one place this vocabulary
    // departs from the general "`delete` always keeps its own key" rule. That rule exists because
    // granting editing while withholding deletion is a real choice people make — and here it is not:
    // every reachable row is a file the caller put there or was sent, and "may use the vault but may
    // not delete their own uploads" is not a grant anyone would write. `llm_conversation` is the same
    // shape and still splits its delete; aligning the two is a follow-up, not a difference with a
    // reason behind it.
    resource: 'vault_file',
    action: 'self',
    displayName: 'Use the Vault',
    description: 'Read, share, change and delete your own vault files',
  },
  {
    resource: 'calendar_event',
    action: 'view',
    displayName: 'View Calendar Events',
    description: 'Read the company calendar',
  },
  {
    resource: 'calendar_event',
    action: 'edit',
    displayName: 'Edit Calendar Events',
    description: 'Create and change events on the company calendar',
  },
  {
    resource: 'calendar_event',
    action: 'delete',
    displayName: 'Delete Calendar Events',
    description: 'Remove an event from the company calendar',
  },
  // PUBLISHING an event, which is not the same audience as authoring one. Legacy `calendar.author` (12
  // active) and `calendar.publisher` (3) map to an identical key set, so the cutover flattened the
  // distinction — but apps/calendar gates its `Publish` checkbox on the publisher name alone, and
  // converting that to `calendar_event.edit` would hand the state transition to all 10 holders of that
  // key. `finalize` because it is the same shape as the timesheet week's transition: the row exists
  // either way, this decides whether it goes live.
  {
    resource: 'calendar_event',
    action: 'finalize',
    displayName: 'Publish Calendar Events',
    description: 'Publish an event so it appears on the company calendar, rather than only authoring it',
  },
  {
    // `CalendarEvent.list` filters unpublished events out for everybody else, so this is the capability
    // that makes the drafts visible at all. Deliberately NOT `calendar_event.edit`, whose audience is
    // identical today: that key means writing, and pointing a read at it makes the drafts disappear the
    // day somebody narrows who may write.
    resource: 'unpublished_calendar_event',
    action: 'view',
    displayName: 'See Unpublished Calendar Events',
    description: 'Read calendar events before they are published, as their author or publisher',
  },
  {
    resource: 'calendar',
    action: 'view',
    displayName: 'View Calendars',
    description: 'Read calendars',
  },
  {
    resource: 'calendar',
    action: 'generate',
    displayName: 'Generate Calendars',
    description: 'Generate calendars',
  },
  {
    resource: 'ai_assistant',
    action: 'view',
    displayName: 'View AI Assistants',
    description: 'Read ai assistants',
  },
  {
    resource: 'ai_assistant',
    action: 'create',
    displayName: 'Create AI Assistants',
    description: 'Add new ai assistants',
  },
  {
    resource: 'ai_assistant',
    action: 'delete',
    displayName: 'Delete AI Assistants',
    description: 'Remove ai assistants',
  },
  {
    resource: 'ai_thread',
    action: 'view',
    displayName: 'View AI Threads',
    description: 'Read ai threads',
  },
  {
    resource: 'ai_thread',
    action: 'edit',
    displayName: 'Edit AI Threads',
    description: 'Create and change AI threads',
  },
  {
    // The NAMED exception to "view keeps its own key" (captain, 2026-08-11): here the whole surface is
    // self-service on your own rows. `LLMConversation.init` and `.list` both filter `UserID = user.id`,
    // there is no admin view over anyone else's conversations, and the model/tool/MCP lists are config.
    // So reading, renaming and sending are one capability — may you use ChatKGPT — and splitting them
    // would only let an admin grant reading your own chats while withholding writing them.
    //
    // Deleting keeps its own key under the general rule. It is worth knowing that this is the weakest
    // case for that rule in the vocabulary: the only rows reachable are the caller's own.
    resource: 'llm_conversation',
    action: 'self',
    displayName: 'Use ChatKGPT',
    description: 'Read, rename and send your own ChatKGPT conversations',
  },
  {
    resource: 'llm_conversation',
    action: 'delete',
    displayName: 'Delete LLM Conversations',
    description: 'Remove your own llm conversations',
  },
  {
    resource: 'prompt',
    action: 'view',
    displayName: 'View Prompts',
    description: 'Read prompts',
  },
  {
    resource: 'prompt',
    action: 'update',
    displayName: 'Edit Prompts',
    description: 'Change prompts',
  },
  {
    resource: 'prompt',
    action: 'delete',
    displayName: 'Delete Prompts',
    description: 'Remove prompts',
  },
  {
    resource: 'license',
    action: 'view',
    displayName: 'View Licenses',
    description: 'Read licenses',
  },
  {
    resource: 'license',
    action: 'delete',
    displayName: 'Delete Licenses',
    description: 'Remove licenses',
  },
  {
    resource: 'license',
    action: 'submit',
    displayName: 'Submit Licenses',
    description: 'Submit for approval licenses',
  },
  {
    resource: 'license',
    action: 'approve',
    displayName: 'Approve Licenses',
    description: 'Approve licenses',
  },
  {
    resource: 'license',
    action: 'reject',
    displayName: 'Reject Licenses',
    description: 'Reject licenses',
  },
  {
    resource: 'license_package',
    action: 'view',
    displayName: 'View License Packages',
    description: 'Read license packages',
  },
  {
    resource: 'license_package',
    action: 'delete',
    displayName: 'Delete License Packages',
    description: 'Remove license packages',
  },
  {
    resource: 'license_package',
    action: 'submit',
    displayName: 'Submit License Packages',
    description: 'Submit for approval license packages',
  },
  {
    resource: 'license_package',
    action: 'approve',
    displayName: 'Approve License Packages',
    description: 'Approve license packages',
  },
  {
    resource: 'license_package',
    action: 'reject',
    displayName: 'Reject License Packages',
    description: 'Reject license packages',
  },
  {
    resource: 'license_project',
    action: 'view',
    displayName: 'View License Projects',
    description: 'Read license projects',
  },
  {
    resource: 'license_project',
    action: 'update',
    displayName: 'Edit License Projects',
    description: 'Change license projects',
  },
  {
    resource: 'license_project',
    action: 'delete',
    displayName: 'Delete License Projects',
    description: 'Remove license projects',
  },
  {
    resource: 'license_project',
    action: 'submit',
    displayName: 'Submit License Projects',
    description: 'Submit for approval license projects',
  },
  {
    resource: 'license_project',
    action: 'approve',
    displayName: 'Approve License Projects',
    description: 'Approve license projects',
  },
  {
    resource: 'license_project',
    action: 'reject',
    displayName: 'Reject License Projects',
    description: 'Reject license projects',
  },
  {
    resource: 'license_scan',
    action: 'view',
    displayName: 'View License Scans',
    description: 'Read license scans',
  },
  {
    resource: 'license_scan',
    action: 'submit',
    displayName: 'Submit License Scans',
    description: 'Submit for approval license scans',
  },
  {
    resource: 'linkedin_change_log',
    action: 'view',
    displayName: 'View LinkedIn Change Logs',
    description: 'Read linkedin change logs',
  },
  {
    resource: 'planner_user_log',
    action: 'view',
    displayName: 'View Planner User Logs',
    description: 'Read planner user logs',
  },
  {
    resource: 'greenhouse_referral',
    action: 'view',
    displayName: 'View Referral Options',
    description: 'Read referral options',
  },
  {
    // The REST route that files the referral, `POST /api/referral`. Separate from `.view` — which the
    // form's company and office dropdowns declare — because the two are a read and a write on the same
    // feature and this vocabulary splits those everywhere else.
    resource: 'greenhouse_referral',
    action: 'create',
    displayName: 'Refer a Candidate',
    description: 'Submit a candidate referral to Greenhouse',
  },
  {
    resource: 'debug_tool',
    action: 'run',
    displayName: 'Run Debug Tools',
    description: 'Trigger a diagnostic that acts on a real system, such as injecting a Sentry test event',
  },

  // ---- Hiring (the hiring subgraph) ----
  {
    resource: 'hiring_candidate',
    action: 'view',
    displayName: 'View Hiring Candidates',
    description: 'Read hiring candidates',
  },
  {
    resource: 'hiring_candidate',
    action: 'edit',
    displayName: 'Edit Hiring Candidates',
    description: 'Create and change hiring candidates',
  },
  {
    resource: 'hiring_test',
    action: 'view',
    displayName: 'View Hiring Tests',
    description: 'Read hiring tests',
  },
  {
    resource: 'hiring_test',
    action: 'edit',
    displayName: 'Edit Hiring Tests',
    description: 'Create and change hiring tests',
  },
  {
    // The two REST verbs on a test's FILES — the ZIP and the images — as against the `hiring_test.*` keys
    // the GraphQL fields declare. Same data family, different door: those fields sit behind
    // `HIRING_APP_DOOR` on `/query`, and these routes sit behind `authMiddleware` and nothing else, so a
    // baseline key would be their whole guard.
    resource: 'hiring_test_file',
    action: 'view',
    displayName: "Download a Hiring Test's Files",
    description: "Download the ZIP holding a hiring test's content (GET /api/hiring/hiringTestDownload)",
  },
  {
    resource: 'hiring_test_file',
    action: 'edit',
    displayName: "Replace a Hiring Test's Files",
    description: 'Upload a hiring test ZIP or overwrite its images (PUT /hiringTestUpload, POST /upsertTestImages)',
  },
  {
    resource: 'hiring_test_assignment',
    action: 'edit',
    displayName: 'Edit Hiring Test Assignments',
    description: 'Create and change hiring test assignments',
  },
  {
    resource: 'hiring_test_assignment',
    action: 'delete',
    displayName: 'Delete Hiring Test Assignments',
    description: 'Remove hiring test assignments',
  },
  {
    resource: 'hiring_test_assignment',
    action: 'send',
    displayName: 'Send Hiring Test Assignments',
    description: 'Send hiring test assignments',
  },
  {
    resource: 'hiring_test_submission',
    action: 'view',
    displayName: 'View Hiring Test Submissions',
    description: 'Read hiring test submissions',
  },
  {
    resource: 'hiring_test_submission',
    action: 'run',
    displayName: 'Run Hiring Test Submissions',
    description: 'Trigger execution of hiring test submissions',
  },
  {
    resource: 'hiring_test_submission',
    action: 'generate',
    displayName: 'Generate Hiring Test Submissions',
    description: 'Generate hiring test submissions',
  },
  {
    // Recording the grade on what a candidate handed in. Split out of `hiring_test.edit`, which the
    // grading mutation shared with `Mutation.hiringTestUpsert` — and a key is as wide as its widest
    // guard, so that one key had to be either the graders (dev/QA/admin) or the test authors (admin),
    // and being the graders left anyone who can grade able to rewrite a rubric and its sample solutions.
    resource: 'hiring_test_submission',
    action: 'rate',
    displayName: "Grade a Candidate's Hiring Test",
    description: "Record the score, result notes and AI-generated flag on a candidate's test submission",
  },
  {
    // The grading audit trail — every previous score with who set it and when. The `audit` action
    // exactly as it is used on the portal's five history panels: strictly a stronger read than the
    // grade itself, so it is graded separately and goes to the hiring admins rather than every grader.
    resource: 'hiring_test_submission',
    action: 'audit',
    displayName: "View a Hiring Test's Grading History",
    description: "Read the before/after of every grade recorded on a candidate's test (HiringCandidateTest.GradingHistory)",
  },
  {
    // The two halves of hiring's dev/QA row filter, which narrows every candidate, test, candidate test
    // and assignment list to the test types the caller works on. Holding both is admin-equivalent for
    // that filter and is how `hiring.admin` reproduces its "sees every row" arm; holding neither returns
    // nothing, which is what the legacy filter does to anyone outside the three roles.
    resource: 'hiring_dev_test',
    action: 'view',
    displayName: 'View Dev Hiring Tests',
    description: 'See hiring candidates, tests, candidate tests and assignments whose test type is dev',
  },
  {
    resource: 'hiring_qa_test',
    action: 'view',
    displayName: 'View QA Hiring Tests',
    description: 'See hiring candidates, tests, candidate tests and assignments whose test type is qa',
  },
  {
    resource: 'hiring_job',
    action: 'run',
    displayName: 'Run Hiring Jobs',
    description: 'Trigger execution of hiring jobs',
  },
  {
    resource: 'slack_channel',
    action: 'view',
    displayName: 'View Slack Channels',
    description: 'Read slack channels',
  },
  {
    resource: 'slack_channel',
    action: 'edit',
    displayName: 'Edit Slack Channels',
    description: 'Create and change slack channels',
  },
  {
    resource: 'slack_channel',
    action: 'delete',
    displayName: 'Delete Slack Channels',
    description: 'Remove slack channels',
  },
  // ---- The per-client dimension ----
  //
  // The first two confer a CLIENT ACCESS LEVEL and are ordinary company-wide grants; the last two are
  // `scope: 'client'` and are asked per account. Together they reproduce, term for term, the two-part
  // rule the nine financial guards apply today: a role list OR `full` client access to that account.
  {
    resource: 'billable_project_report',
    action: 'view',
    displayName: 'Report on Billable Projects',
    description: 'Include billable projects in a report over many projects',
  },
  {
    resource: 'nonbillable_project_report',
    action: 'view',
    displayName: 'Report on Non-Billable Projects',
    description: 'Include non-billable projects in a report over many projects',
  },
  {
    resource: 'historical_project_report',
    action: 'view',
    displayName: 'Report on Inactive Engagements',
    description: 'Include closed and inactive engagements in a report over many projects',
  },
  {
    resource: 'internal_project_report',
    action: 'view',
    displayName: 'Report on Internal Projects',
    description: 'Include internal projects in a report over many projects',
  },
  {
    resource: 'revenue_report',
    action: 'view',
    displayName: 'View the Revenue Reports',
    description: 'Read revenue by client on the admin dashboard and revenue by region on Team Rates',
  },
  {
    resource: 'utilization_report',
    action: 'view',
    displayName: 'View the Utilization Report',
    description: 'Read company-wide hours by month and billing type, overall and per office, on Team Utilization',
  },
  {
    // The two reach tiers, and the whole of what they say: how much you get on a client you are NOT on
    // the service team of. Neither is needed to reach a client you ARE on — that assignment alone
    // yields `full`, holding no key.
    resource: 'unassigned_clients',
    action: 'full',
    displayName: 'Full Access to Unassigned Clients',
    description: 'Full client access to every client, without being on its service team',
  },
  {
    // `limited` on a client you are not on. It ALSO raises a `limited` service-team row to `full`, which
    // is inert today — every `accountserviceteam` row is `full` and no surface writes any other value —
    // and is still the code path clientPermissionGate.ts's `assigned` branch exists for.
    resource: 'unassigned_clients',
    action: 'limited',
    displayName: 'Limited Access to Unassigned Clients',
    description: 'Limited client access to every client, without being on its service team',
  },
  {
    resource: 'all_document_access',
    action: 'view',
    displayName: "Read Every Client's Documents",
    description: 'Read documents for every client, without being on its service team',
  },
  {
    // The same bypass on the CONTRACT PIPELINE, and its own resource for the reason `all_meeting_access`
    // has one: the branch it replaces admits `crm.finance` as well as `crm.admin`, where
    // `unassigned_clients.full` is granted to `crm.admin` alone. Widening that key instead would hand
    // crm.finance every other client-scoped surface too.
    resource: 'all_contract_process_access',
    action: 'view',
    displayName: "Read Every Client's Contract Processes",
    description: 'Read and change contract processes for every client, without being on its service team',
  },
  {
    // The client-scope bypass on the MEETING surface. `Meeting.list` has a branch that skips the
    // client-service-team subquery entirely, and its audience is one person wider than
    // `unassigned_clients.full` because it also admits crm.finance. Its own resource rather than a second
    // grant on that key, which nine other guards read.
    resource: 'all_meeting_access',
    action: 'view',
    displayName: "Read Every Client's Meetings",
    description: 'Read meetings for every client, without being on its service team',
  },
  {
    // The same bypass on the INVOICE list, and its own resource for two reasons rather than one:
    // `unassigned_clients.full` admits `crm.admin` alone where this branch also admits `crm.finance`, AND
    // that key is granted to the `portal.superadmin` group, which would satisfy it for a superadmin the
    // `withoutBypass` refusal has already turned away.
    resource: 'all_invoice_access',
    action: 'view',
    displayName: "Read Every Client's Invoices",
    description: 'Read invoices for every client, without being on its service team',
  },
  {
    // The same bypass on the ACTION-ITEM list. Its audience IS `unassigned_clients.full`'s, and it is
    // still a separate key: that one is granted to the `portal.superadmin` group, and this guard refuses
    // the platform bypass, so sharing would hand the branch to a superadmin who holds no CRM name.
    resource: 'all_action_item_access',
    action: 'view',
    displayName: "Read Every Client's Action Items",
    description: 'Read action items for every client, without being on its service team',
  },
  {
    resource: 'client_revenue',
    action: 'view',
    displayName: 'View Client Revenue',
    description: "A client's revenue target, revenue metrics and account forecast",
    scope: 'client',
  },
  {
    resource: 'client_billing',
    action: 'view',
    displayName: 'View Client Billing',
    description: "The invoices and purchase orders raised against a client's engagement",
    scope: 'client',
  },
  {
    // The rate-card half of the same rule, and separate from `client_billing.view` because the guards do
    // not admit the same people: the three `rateCards()` methods take `crm.admin` OR `crm.finance`, the
    // invoice and purchase-order guards take `crm.finance` alone. One key for both has to be granted the
    // union, which hands `crm.admin` fourteen invoice and purchase-order guards it cannot reach today.
    resource: 'client_rate_card',
    action: 'view',
    displayName: "View a Client's Rate Cards",
    description: "The rate cards attached to a client, its programs and its engagements",
    scope: 'client',
  },
  {
    // The finance half of the document and contract-template rules: `crm.finance` reaches any client's
    // records, and everybody else needs client access to that one account. `scope: 'client'` like the two
    // above, but it names a CAPABILITY rather than the data, because the same company-wide arm sits over
    // two unrelated data families and its audience is the only thing they share.
    resource: 'finance_client_access',
    action: 'view',
    displayName: 'Finance Access to Client Records',
    description: "Reach a client's documents and contract templates as Finance, without being on its service team",
    scope: 'client',
  },
  // ---- The person-scoped object-type field guards ----
  //
  // These four are read by class methods on `Person`, not by a `@requiresPermission` declaration, so
  // nothing in fieldPermissions.test.ts will notice if one goes unused. Each replaces the permission
  // half of one guard and is granted to exactly the groups named after the legacy permissions that
  // guard tests, so its holder set equals the set the guard admits today.
  //
  // Four rather than eight because two of the eight already had a key with the same meaning AND
  // provably the same holders — `client_service_team.view` and `work_history.view` — and minting a
  // synonym for either would have been dead vocabulary.
  {
    resource: 'person_detail',
    action: 'view',
    displayName: 'View Employee Detail Fields',
    description: "Another employee's review dates, contract date, check-ins, PTO carryover and personal contact details",
  },
  {
    resource: 'sick_day',
    action: 'view',
    displayName: 'View Sick Day Balances',
    description: "Another employee's sick day balance and the sick leave making it up",
  },
  {
    resource: 'mentorship',
    action: 'view',
    displayName: 'View Mentorships',
    description: "Another employee's mentor and mentorship start date",
  },
  {
    resource: 'collaboration',
    action: 'view',
    displayName: 'View Collaboration History',
    description: 'When two other employees last worked together, and on what',
  },
  {
    resource: 'person_profile',
    action: 'update',
    displayName: "Edit Another Employee's Profile",
    description: "Change another employee's phone, about, internal bio, pronouns or birthday",
  },

  {
    resource: 'person_visibility',
    action: 'update',
    displayName: 'Hide People From the Directory',
    description: "Set or clear a person's Hidden flag, which removes them from the directory",
  },
  {
    resource: 'org_structure',
    action: 'view',
    displayName: 'View the Organisation Structure',
    description: 'The org chart, reporting structure, leadership coverage and candidate finder',
  },
  {
    resource: 'person_record',
    action: 'update',
    displayName: "Edit a Person's Record",
    description: "Change another employee's record from the admin dashboard, including their job title and office",
  },
  {
    resource: 'person_email',
    action: 'update',
    displayName: "Change a Person's Email Address",
    description: "Replace another employee's login email address",
  },
  {
    // The mentorship, education and prior-experience tier of `Person`'s constructor. Not
    // `mentorship.view`, which is `directory.manager` and reaches a different 109 people: it would widen
    // and narrow at once.
    resource: 'person_background',
    action: 'view',
    displayName: "View Another Person's Background",
    description: "Another employee's mentor, education, prior experience and LinkedIn profile",
  },
  {
    // The WRITE half of the same fields, plus the years-of-prior-experience number the Polaris
    // team-member form sends. Its guard lists three roles where the read tier lists two, so this key is
    // granted to MORE people than `person_background.view` — that asymmetry is the guards' and is
    // reproduced rather than tidied away.
    resource: 'person_background',
    action: 'update',
    displayName: "Edit Another Person's Background",
    description: "Another employee's education, prior experience and years of experience before Konrad",
  },
  {
    // The two resourcing notes, Experience Notes and Resourcing Profile Notes, split out of the two keys
    // above: one key to see them and to change them. Granted to Portal Admin alone; managers reach their
    // own reporting line through the resolver's manager arm, and the person themselves does not reach
    // their own notes at all.
    resource: 'person_background',
    action: 'manage',
    displayName: "Manage a Person's Resourcing Notes",
    description: "See and edit another employee's Experience Notes and Resourcing Profile Notes",
  },
  {
    // The direct per-user grants, as against the group membership `permission_group.assign` covers.
    // `Person.permissionsApply` and `Person.groupsApply` are two halves of one mutation and name
    // different lists — 19 against 61 — so one key cannot serve both without widening the narrower.
    resource: 'person_permission',
    action: 'assign',
    displayName: 'Assign Permissions to a Person',
    description: 'Grant or revoke a permission on one employee directly, outside their permission groups',
  },
  {
    resource: 'person_expertise',
    action: 'update',
    displayName: "Edit Another Person's Certifications and Professional Skills",
    description: "Change the certifications and professional skill tags on somebody else's record",
  },
  {
    resource: 'person_skill',
    action: 'update',
    displayName: "Edit Another Person's Personal Skill Tags",
    description: "Change the self-declared personal skill tags on somebody else's record",
  },
  {
    resource: 'person_discipline',
    action: 'update',
    displayName: "Edit Another Person's Disciplines",
    description: "Change which disciplines somebody else is assigned to",
  },
  {
    resource: 'project_link',
    action: 'view',
    displayName: "Read a Project's Links",
    description: "Read the links attached to an engagement (projectLink, projectLinks)",
  },
  {
    resource: 'team_timesheet',
    action: 'review',
    displayName: "Review a Team Member's Timesheet",
    description: "Read another person's timesheet, set their week state, and flag issues on it",
  },
  {
    resource: 'team_timesheet',
    action: 'edit',
    displayName: "Edit a Team Member's Timesheet",
    description: "Change another person's entries and notes, including in an approved or finalized week",
  },
  {
    resource: 'team_timesheet',
    action: 'finalize',
    displayName: "Finalize a Team Member's Week",
    description: "Close another person's timesheet week",
  },
  {
    resource: 'team_timesheet',
    action: 'triage',
    displayName: 'Work the Timesheet Reconciliation Queue',
    description: 'Read the queue of flagged and autofill-skipped weeks across the company',
  },

  // ---- The REST upload and download surfaces ----
  //
  // Same device as the four above, and it exists for the same reason: these routes carry no
  // `@requiresPermission`, so nothing derives their key from them. Each was originally pointed at the
  // key its GraphQL neighbours declare — `person.update_self` for the photo route, `document.update` for
  // the Polaris document upload — and that quietly handed the route the neighbours' holder set, which
  // is not the set the route admits today and in two cases was the whole company. Each key below is
  // granted exactly the legacy list at its own guard site, so the route's population is its own.
  {
    resource: 'person_photo',
    action: 'update',
    displayName: "Edit Another Employee's Photo",
    description: 'Replace the profile photo of an employee other than yourself (POST /uploadV2/photo)',
  },
  {
    resource: 'pto_rollover',
    action: 'update',
    displayName: "Edit a Person's PTO Rollover Dates",
    description: 'Change the rollover and next-rollover dates that decide when PTO accrues',
  },
  {
    resource: 'pto_rollover',
    action: 'view',
    displayName: 'View the PTO Rollover Log',
    description: 'Read the change log of PTO rollover runs and manual rollover-date edits',
  },
  {
    resource: 'pto_report',
    action: 'view',
    displayName: 'View PTO Balance Reports',
    description: "Read other people's PTO balances as of a date (ptoBalanceAsOfTodayReport)",
  },
  {
    resource: 'pto_report',
    action: 'export',
    displayName: 'Download the PTO Report',
    description: 'Download a day of the PTO report as CSV (GET /reports/:year/:month/:day)',
  },
  // Two more of the same, and the reason is worth stating once because it is the trap the whole
  // vocabulary has: a key is a shared object granted to GROUPS, so a key named by two guards must be
  // granted the union of their audiences and the narrower guard silently inherits the wider one. These
  // two routes were pointed at their feature's key and picked up its whole audience — the
  // certification image +115 people, the client logo +82. Each names its own key now, granted exactly
  // the legacy list at its own guard site.
  {
    resource: 'certification_image',
    action: 'update',
    displayName: "Edit a Certification's Image",
    description: 'Replace the image shown for a certification (PUT /uploadV2/:id/certificationimageupload)',
  },
  {
    resource: 'client_logo',
    action: 'update',
    displayName: "Edit a Client's Logo",
    description: 'Replace the logo shown for a client account (PUT /uploadV2/:id/clientlogoupload)',
  },

  // ---- The entry points with no GraphQL surface ----
  //
  // The bug-report REST route, the MCP surface, and the hiring subgraph's endpoint. None of them
  // can carry a @requiresPermission declaration, so each names both sets inline at its own guard site
  // (checkPermission / checkPermissionForRoute in the portal, the Apollo context function in hiring).
  //
  // The two app doors are the reason the old system cannot simply be deleted: they are the only
  // base-app permissions with real server-side enforcement, so until these keys exist and are granted,
  // deleting `permissions_v2` takes the door off the MCP surface and off hiring's whole endpoint. See
  // server/graph/lib/services/permissionsTransition/AGENTS.md.
  {
    resource: 'bug_report',
    action: 'create',
    displayName: 'Report a Bug',
    description: 'File a bug report or feature request from the in-app widget',
  },
  {
    resource: 'work_from_anywhere_limit',
    action: 'five_days',
    displayName: 'Flexible Work: 5 Day Limit',
    description: 'Caps work from anywhere at 5 days, with no in-office limit. Was travel.limit5',
  },
  {
    resource: 'work_from_anywhere_limit',
    action: 'ten_days',
    displayName: 'Flexible Work: 10 Day Limit',
    description: 'Caps work from anywhere at 10 days, with no in-office limit. Was travel.wfa5 and travel.wfa10',
  },
  {
    resource: 'work_from_anywhere_limit',
    action: 'no_days',
    displayName: 'Flexible Work: No Days',
    description: 'No work from anywhere and no in-office days. Was travel.limit0',
  },
  {
    resource: 'app',
    action: 'mcp',
    displayName: 'Use the MCP Server',
    description: 'The whole MCP surface: connecting to the server, its OAuth consent, and every tool behind it',
  },
  {
    resource: 'app',
    action: 'hiring',
    displayName: 'Use the Hiring App',
    description: "The hiring subgraph's GraphQL endpoint. Its individual fields still need their own permissions",
  },
  // The Agent Hub door: its run list, badge counts, a run's detail, marking one viewed, starting one, and
  // rating its output. All six root fields declare THIS, and there is NOTHING past your own rows — every
  // row-level check in the feature is `run.UserID === your id`, with no override for portal.admin and none
  // for portal.superadmin either (captain, 2026-08-19).
  //
  // One door for six fields is deliberate rather than unexamined. Reading a run versus starting one —
  // which spends money — needs no second key, because a run only exists if you passed the create path
  // first: the workflow's own permission plus client scope, enforced at upload (services/agent/access.ts).
  // Its audience is the two agent workflow permissions, which is what `hasAgentHubAccess` names.
  {
    resource: 'app',
    action: 'agent',
    displayName: 'Use the Agent Hub',
    description: 'Read your own agent runs, start them, and rate their output',
  },
  {
    resource: 'app',
    action: 'pto',
    displayName: 'Use the PTO App',
    description: 'The PTO app door. Its individual surfaces still need their own permissions',
  },
  {
    resource: 'app',
    action: 'work_from_anywhere',
    displayName: 'Use the Travel App',
    description: 'The Travel app door. Its individual surfaces still need their own permissions',
  },
  {
    resource: 'app',
    action: 'polaris',
    displayName: 'Use Polaris',
    description: 'The Polaris app door. Its individual surfaces still need their own permissions',
  },
  // The Calendar door admits three legacy names, not one: `app.calendar`, `calendar.author` and
  // `calendar.publisher` each open the app (apps/calendar/src/App.tsx). All three map to this key, so
  // the door states its own disjunction rather than relying on the two author names happening to be a
  // subset of `app.calendar` on today's data — which they are.
  {
    resource: 'app',
    action: 'calendar',
    displayName: 'Use the Calendar App',
    description: 'The Calendar app door. Authoring and publishing events still need their own permissions',
  },
  {
    resource: 'app',
    action: 'vault',
    displayName: 'Use the Vault App',
    description: 'The Vault app door. The externally-shared vault is a separate, deliberately ungated surface',
  },
  {
    resource: 'app',
    action: 'myteam',
    displayName: 'Use the My Team App',
    description: 'The My Team app door. Reading a particular team or review still needs its own permission',
  },
  {
    resource: 'app',
    action: 'assetcontrol',
    displayName: 'Use the Asset Tracker',
    description: 'The Asset Control app door. Its admin surface is a separate permission (asset.edit)',
  },
  {
    resource: 'app',
    action: 'directory',
    displayName: 'Use the Directory',
    description: 'The Directory app door. Granted to everybody today; it exists so that can change',
  },
  {
    resource: 'app',
    action: 'handbook',
    displayName: 'Use the Handbook',
    description: 'The Handbook app door. Granted to everybody today; it exists so that can change',
  },
  {
    resource: 'app',
    action: 'it',
    displayName: 'See the Service Desk Link',
    description: 'Shows the Jira service desk in the nav. Jira controls who may actually open it',
  },
  {
    resource: 'app',
    action: 'timetracker',
    displayName: 'Use the Project Tracker',
    description: 'The Project Tracker app door. Tracking your own hours needs no permission beyond it',
  },
  {
    resource: 'app',
    action: 'chatkgpt',
    displayName: 'Use ChatKGPT',
    description: 'The ChatKGPT app door. The conversations it reads are baseline, so this is the only gate',
  },
  {
    resource: 'app',
    action: 'licensetracker',
    displayName: 'Use the Licence Tracker',
    description: 'The Licence Tracker app door. Every licence, package, project and scan action has its own key',
  },
  {
    resource: 'app',
    action: 'planner',
    displayName: 'Use the Planner',
    description: 'The Polaris Planner surface. Its incoming and people tabs still need their own permissions',
  },
  {
    resource: 'app',
    action: 'admin',
    displayName: 'Use the Admin App',
    description: 'The Admin app door. Every panel inside it still needs its own permission',
  },

  // ---- Asset Control ----
  //
  // Both keys are enforced in server/asset-manager-backend's own middleware, not by a directive: it is a
  // separate service over REST with its own database, so there is no field to declare them on.
  //
  // `asset.edit` covers that service's WHOLE admin surface — its 60 `checkAdmin` routes, 16 of which are
  // reads. Naming a read `edit` is deliberate and is the narrower of two wrongs: the alternative was to
  // gate those reads on `asset.view`, which the row-scope tier below already holds, and that would hand
  // the 12 tier holders 16 admin routes they cannot reach today. Splitting the admin surface properly
  // needs a third key whose only honest name is a non-verb, and the vocabulary allows exactly two of
  // those (app.mcp, app.hiring). Revisit if Asset Control ever gains a read-only admin role.
  {
    resource: 'asset',
    action: 'edit',
    displayName: 'Administer Asset Control',
    description: 'The Asset Control admin surface: assets, accessories, locations, templates, reports and alerts',
  },
    // The four permission-only arms of `PTORequest.list` — PTO administrator, travel moderator, PTO
    // approver, PTO reports viewer. They are one door, not four: none pairs with a structural predicate,
    // so their disjunction IS this audience and writing it once is not a widening.
  {
    resource: 'team_pto_request',
    action: 'view',
    displayName: "View Anyone's PTO Requests",
    description: "Read another person's PTO requests without managing them",
  },
  {
    resource: 'team_pto_request',
    action: 'review',
    displayName: "Manage Your Reports' PTO Requests",
    description: 'See and act on the PTO requests of people you manage, including unapproved ones',
  },
  {
    resource: 'team_pto_request',
    action: 'create',
    displayName: 'Create PTO Requests for Others',
    description: "Enter a PTO request on another person's behalf",
  },
  {
    resource: 'team_pto_request',
    action: 'reject',
    displayName: "Decline Another Person's PTO Request",
    description: 'Decline a PTO request somebody else submitted',
  },
  {
    resource: 'team_pto_request',
    action: 'approve',
    displayName: "Approve Another Person's PTO Request",
    description: 'Approve a PTO request somebody else submitted',
  },
  {
    // `override` rather than `edit`: this is the administrative tier, not the create-and-change fold.
    // `team_pto_request.create` beside it is a different, wider audience — entering a request on
    // somebody's behalf — so folding the two would hand every PTO manager the manager-rule bypass.
    resource: 'team_pto_request',
    action: 'override',
    displayName: 'Administer PTO Requests',
    description: 'Rewrite PTO requests across a date range, and override the manager rules',
  },
  {
    // THE RESOURCING TRIAGE TIER, minted by the cutover-readiness audit for one audience and no guard:
    // `unreviewedPTOEmail` mails an engagement lead about their team's unreviewed PTO only if that lead
    // holds `resourcing.manager` or `resourcing.admin`, and its own helper is called
    // `userHasTriagePermissions`. Nothing authorises anybody here — it decides who gets mail.
    //
    // It is a MINT rather than a reuse because NO key in the vocabulary reaches that audience: measured
    // across all of them, nothing came within four people of the 12 the two names reach. `pto_request.triage`
    // is the near miss to know about and is not it — its preimage is `planner.admin`, `pto.manager` and
    // `resourcing.admin`, which is 110 people and does not include `resourcing.manager` at all.
    resource: 'team_pto_request',
    action: 'triage',
    displayName: "Triage Your Engagements' PTO Requests",
    description: "Be notified of, and act on, unreviewed PTO from people on engagements you lead",
  },
  // The SEE-EVERYONE arm of a PTO balance read. `Query.ptoCalc` and `Query.ptoEstimate` took a baseline
  // PTO read key and checked nothing of their own, so every employee could read any colleague's
  // balance, accrual and carryover by naming their id — while `sickDayBalance`, returned by the SAME
  // resolver, already refused anyone but the person, their manager chain and `sick_day.view`. Granted to
  // `pto.admin` alone, because the PTO app let only them pick anybody; a `pto.manager` reaches their own
  // reporting tree through the resolver's manager arm instead.
  {
    resource: 'team_pto_balance',
    action: 'view',
    displayName: "View Anyone's PTO Balance",
    description: "Read another person's PTO balance, accrual and carryover without managing them",
  },
  // The SEE-EVERYONE arm of a FLEXIBLE WORK allowance read, and `team_pto_balance.view`'s exact
  // counterpart: `Query.availableFlexibleWork` took the baseline `work_from_anywhere_request.view` and checked nothing,
  // so every employee read any colleague's remaining work-from-anywhere and work-from-office days by naming
  // their id. Granted `pto.admin` and `pto.manager`, and NOT folded onto the PTO balance key, because a
  // flexible-work allowance and a PTO balance are two entitlements.
  {
    resource: 'team_work_from_anywhere_balance',
    action: 'view',
    displayName: "View Anyone's Flexible Work Allowance",
    description: "Read another person's remaining work from anywhere and work from office days without managing them",
  },
  {
    resource: 'pto_offset',
    action: 'create',
    displayName: 'Create a PTO Offset',
    description: 'Adjust a PTO balance with an offset entry, including your own',
  },
  // The My Team override, and the successor to legacy `teams.superuser` AND to the `app.myteam`
  // short-circuit, which made every holder anyone's manager in My Team. Every site reads
  // `person.isSubordinateOfCurUser || teams.superuser`, so the name stands as an administrative bypass of
  // a RELATIONSHIP — exactly the shape that must be its own key rather than an `app.*` door that would
  // also make its holders managers at every other guard. The server honours it beside the reporting line
  // at the review-date read and write and the four review guards. It belongs to the admins alone: a
  // manager is limited to their own reports, the people My Team shows them, which the reporting line
  // already admits (captain, 2026-10-28). The cutover still maps both legacy names to it, and
  // 20261029-PRE-3-access-group-trims.sql takes it back off the Team Manager roles.
  {
    resource: 'team_review',
    action: 'override',
    displayName: "Act as Anyone's Manager in My Team",
    description:
      "See and change anybody's check-in and review dates, and add, edit and delete their reviews, without managing them",
  },
  {
    resource: 'all_team_review_access',
    action: 'view',
    displayName: 'See Everyone in the My Team Lists',
    description:
      'List every employee in My Team and Annual Reviews & Check Ins, with the department filter, rather than your own team',
  },
  {
    resource: 'review',
    action: 'override',
    displayName: 'Override the Review Manager Lock',
    description: 'Edit, delete or read a review you do not manage — the Employee Services override',
  },
  {
    resource: 'agent_msa_review',
    action: 'run',
    displayName: 'Run the MSA Review Workflow',
    description: 'Start an agent run against an uploaded MSA',
  },
  {
    resource: 'agent_nda_review',
    action: 'run',
    displayName: 'Run the NDA Review Workflow',
    description: 'Start an agent run against an uploaded NDA',
  },
  {
    resource: 'agent_deck_generation',
    action: 'run',
    displayName: 'Run the Deck Generation Workflow',
    description: 'Start a deck generation run from a prompt, in Polaris or through the MCP server',
  },
  {
    resource: 'agent_run',
    action: 'override',
    displayName: "Act on Anyone's Agent Run",
    description: "Agent Admin: review every user's agent runs, usage and prompts, and read, rate or stop a run that is not yours",
  },
  {
    resource: 'agent_workflow_setting',
    action: 'edit',
    displayName: 'Edit Agent Workflow Settings',
    description: "Agent Admin: read and change each agent workflow's spend limit, follow-up cutoffs, model, effort and file limits",
  },
  // Workspaces (docs/workspaces W10, W16), named as its legacy permissions are (see the resource). `manage`
  // and `admin` stand alone like `agent_run.override` beside the workflow keys: either opens the
  // Workspaces section without `use`, and neither starts a paid run without it.
  {
    resource: 'workspaces',
    action: 'use',
    displayName: 'Workspaces',
    description: 'Required to make decks: be a member of team workspaces, act by your role in each, and use the Workspaces tools over MCP',
  },
  {
    resource: 'workspaces',
    action: 'manage',
    displayName: 'Manage Workspaces',
    description: 'Create team workspaces, see every one listed, and manage the members, name, client link and deletion of those you belong to',
  },
  {
    resource: 'workspaces',
    action: 'admin',
    displayName: 'Workspaces Admin',
    description: 'Read and manage every workspace, document and version without being a member, and act as an Editor in each',
  },
  {
    resource: 'asset',
    action: 'view',
    displayName: 'View Shared Assets',
    description: "See QA shared devices in your own office country, beyond the assets assigned to you",
  },

  // ---- The bypass ----
  //
  // Declared like any other key so it is granted, revoked and audited by the machinery that already
  // exists. It is enforced NOWHERE by a directive: no field declares it, because it is not a
  // requirement — it is what passes every requirement. `permissionGate.ts` is the only reader.
  {
    resource: 'portal',
    action: 'superadmin',
    displayName: 'Portal Superadmin',
    description: 'Passes every permission check without the permission being granted. The successor to legacy portal.superadmin',
  },
] as const satisfies readonly PermissionDefinition[];

/** Distributes over the union of definitions, so each pair contributes exactly its own key. */
type KeyOf<Definition> = Definition extends { resource: string; action: string }
  ? `${Definition['resource']}.${Definition['action']}`
  : never;

/**
 * The canonical key of a permission: `<resource>.<action>`, over the declared pairs only.
 * `timesheet.finalize` types because it is declared; `timesheet.export` does not, even though both
 * halves exist. That is the point — an invalid permission is a compile error, and renaming one is a
 * type error at every call site rather than a silent runtime miss.
 */
export type PermissionKey = KeyOf<(typeof permissionDefinitions)[number]>;

/** Builds the canonical key from a pair, typed as the exact key rather than a widened string. */
export const permissionKey = <Definition extends { resource: PermissionResource; action: PermissionAction }>(
  definition: Definition,
): `${Definition['resource']}.${Definition['action']}` =>
  `${definition.resource}.${definition.action}` as `${Definition['resource']}.${Definition['action']}`;

/** Every declared key, for runtime membership checks. Order matches `permissionDefinitions`. */
export const PERMISSION_KEYS: readonly PermissionKey[] = permissionDefinitions.map((p) =>
  permissionKey(p),
) as readonly PermissionKey[];

const PERMISSION_KEY_SET: ReadonlySet<string> = new Set(PERMISSION_KEYS);

/** Distributes like `KeyOf`, but only over the definitions carrying `scope: 'client'`. */
type ClientScopedKeyOf<Definition> = Definition extends { resource: string; action: string; scope: 'client' }
  ? `${Definition['resource']}.${Definition['action']}`
  : never;

/**
 * The keys whose check takes a client account id. A distinct type rather than a runtime convention, so
 * handing one to an account-less check — where the company-wide half would decide alone, for every
 * client — is a compile error at the call site rather than a widening nobody notices.
 */
export type ClientScopedPermissionKey = ClientScopedKeyOf<(typeof permissionDefinitions)[number]>;

/** The same set at runtime, for the assertions that keep these off the root-field gate. */
export const CLIENT_SCOPED_PERMISSION_KEYS: readonly ClientScopedPermissionKey[] = permissionDefinitions
  .filter((p) => 'scope' in p && p.scope === 'client')
  .map((p) => permissionKey(p)) as readonly ClientScopedPermissionKey[];

const CLIENT_SCOPED_KEY_SET: ReadonlySet<string> = new Set(CLIENT_SCOPED_PERMISSION_KEYS);

/** Narrows a declared key to one that must be asked per account. */
export const isClientScopedPermissionKey = (value: string): value is ClientScopedPermissionKey =>
  CLIENT_SCOPED_KEY_SET.has(value);

/**
 * THE CLIENT REACH TIERS, named here rather than at either evaluator because BOTH SUBGRAPHS AND THE
 * BROWSER read them and the account axis is decided identically in all three.
 *
 * They answer one question — `what do you get on a client you are NOT on the service team of?` — and
 * being ON a client's service team is a separate axis that needs no key at all. `unassigned_clients.full`
 * mirrors what legacy `crm.admin` and `portal.superadmin` do to `User.clientPermission`;
 * `unassigned_clients.limited` mirrors `crm.srmanager`, and additionally raises an assigned client's row
 * to `full`.
 *
 * A literal in a third place is what this pair exists to stop: `clientPermissionGate.ts` decides the
 * server's answer and `permissionChecks.ts` decides the browser's, and a rename that reached only one of
 * them would move an audience silently.
 */
export const UNASSIGNED_CLIENTS_FULL_KEY = 'unassigned_clients.full' satisfies PermissionKey;

/** See `UNASSIGNED_CLIENTS_FULL_KEY`. */
export const UNASSIGNED_CLIENTS_LIMITED_KEY = 'unassigned_clients.limited' satisfies PermissionKey;


/**
 * The contract-review workflows, as KEYS, for the browser.
 *
 * It REPLACED `CONTRACT_REVIEW_PERMISSIONS` in permissionList.ts rather than sitting beside it. That
 * constant was written as the legacy twin the server would keep naming, and the server never did: the
 * agent guard names one key per WORKFLOW (see `AGENT_WORKFLOWS`), which is a different question.
 * So it had no reader at all once the browser moved, and it is deleted.
 *
 * NOT the same question as opening the Agent Hub, which is `app.agent` and is derived from holding ANY
 * workflow permission. Gating the client Contract Review page on the hub door would admit somebody whose
 * only workflow lives elsewhere and hand them a document-type picker with nothing in it.
 */
export const CONTRACT_REVIEW_KEYS: NonEmptyPermissions<PermissionKey> = [
  'agent_msa_review.run',
  'agent_nda_review.run',
];

/**
 * Narrows an arbitrary string — a row read back from the database, say — to a declared key.
 * A name this file does not declare is not a permission, so it grants nothing.
 */
/**
 * Any one of these opens the Workspaces section, in the browser's nav and routes and on the server.
 * `workspaces.manage` and `workspaces.admin` stand alone and do not require `workspaces.use` (docs/workspaces
 * W14, W16).
 */
export const WORKSPACES_SECTION_KEYS: NonEmptyPermissions<PermissionKey> = [
  'workspaces.use',
  'workspaces.manage',
  'workspaces.admin',
];

export const isPermissionKey = (value: string): value is PermissionKey => PERMISSION_KEY_SET.has(value);

// permissions_transition column limits — must match the Permission entity and the migration.
// Throws at import time so the server can't boot (and CI fails) with values that would be truncated
// or with a duplicate pair that the unique key would reject.
const seen = new Set<string>();
for (const p of permissionDefinitions) {
  const name = permissionKey(p);

  if (seen.has(name)) {
    throw new Error(`Permission "${name}" is declared more than once`);
  }
  seen.add(name);

  if (name.length > 100) {
    throw new Error(`Permission "${name}" key exceeds 100 chars (got ${name.length})`);
  }
  if (p.displayName.length > 100) {
    throw new Error(`Permission "${name}" displayName exceeds 100 chars (got ${p.displayName.length})`);
  }
  if (p.description.length > 200) {
    throw new Error(`Permission "${name}" description exceeds 200 chars (got ${p.description.length})`);
  }
}

export default permissionDefinitions;
